// Summary: Main POS orders workspace handling cart, payment, tips, transfer, and diners flows.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
import React, { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Button, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Button as SharedButton } from '../../../../../components/Button';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCommentDots, faDeleteLeft, faPen, faPrint, faShekelSign, faShieldHalved, faStar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { derivePaymentScreenTip } from '../tip/tip.utils';
import type { OrderPrintLogView, OrderView } from '../../../../../services/orders/orders.service';
import { isQuickChargeOrderCode, resolveTip, roundMoney } from 'shared-models/browser';
import type { HappyHourView } from 'shared-models/browser';
import { ordersService } from '../../../../../services/orders/orders.service';
import type { CategoryView } from '../../../../../services/categories/categories.service';
import {
  buildAncestorRails,
  buildCategoryRail,
  buildMenuProductGroups,
  type CategoryFilter,
} from '../menu/category-menu.utils';
import { orderlinesService, orderlineDisplayName, type OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import type { SplitPaymentlinePayload, TipPayload, SplitAndPayRequest } from '../../../../../services/payments/payments.service';
import type { ProductView } from '../../../../../services/products/products.service';
import { OrdersTable } from '../table/OrdersTable';
import { OrdersFilterCards } from '../orders-filter/OrdersFilterCards';
import { buildOrdersFilterCounts, filterOrders, type OrdersFilterKey } from '../orders-filter/orders-filter.utils';
import { OrderActionsModal } from '../order-actions/OrderActionsModal';
import { OrderPrintLogItemsModal } from '../order-print-log/OrderPrintLogItemsModal';
import { OrderPrintLogModal } from '../order-print-log/OrderPrintLogModal';
import { GeneralItemModal, type GeneralItemPayload } from '../general-item/GeneralItemModal';
import { DepositReturnModal } from '../../receipt/deposit-return/DepositReturnModal';
import { ReceiptScreen } from '../../receipt/screen/ReceiptScreen';
import { OrderSummary } from '../../receipt/summary/OrderSummary';
import { ReceiptTopBar } from '../../receipt/topbar/ReceiptTopBar';
import { ReceiptActionsModal } from '../../receipt/actions/ReceiptActionsModal';
import { GlobalDiscountModal } from '../../receipt/actions/global-discount-modal/GlobalDiscountModal';
import { ItemDiscountModal } from '../../receipt/actions/item-discount-modal/ItemDiscountModal';
import { ItemDiscountSelectModal } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import type { OrderlineSelection } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import orderClasses from './order-detail.module.scss';
import { formatMoney, formatMoneyCompact } from '../../../../../utils/money';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { splitOrderlineSelections, groupOrderlineSelections, groupOrderlineExtraTotal, type ComboSelectionRoute } from '../../../../../utils/orderline-selections';
import { buildKitchenPrintGroups } from './kitchen-print-groups.util';
import type { KitchenFireDispatchRequest } from '../../../hooks/useKitchenFireAutoPrint';
import { NumPadModal } from '../../../../../components/NumPad/NumPad';
import { NumericKeypad } from '../../../../../components/Keypad/NumericKeypad';
import { SplitPaymentDialog } from '../split-payment/SplitPaymentDialog';
import { KitchenNoteModal } from '../kitchen-note/KitchenNoteModal';
import { PaymentActionsRow, QuickPaymentMethods } from '../split-payment/PaymentActionsRow';
import type { PaymentMethodTap } from '../split-payment/PaymentActionsRow';
import { tenderKindLabel } from '../../../../../utils/credit-method-label';
import { AmountEntryDialog } from '../split-payment/AmountEntryDialog';
import { CashChangeDialog } from '../split-payment/CashChangeDialog';
import type { PaymentTerminalView } from '../../../../../services/credit-payments/credit-payments.service';
import type { SessionView } from '../../../../../services/sessions/sessions.service';
import { kdsService } from '../../../../../services/kds/kds.service';
import { printersService } from '../../../../../services/printers/printers.service';
import { KdsPrintTicket, type KdsPrintTicketLine } from '../../../../KdsPage/components/print/KdsPrintTicket';
import {
  captureKdsReceiptSnapshot,
} from '../../../../KdsPage/utils/kds-page';
import kdsPageClasses from '../../../../KdsPage/styles/kds-page.module.scss';
import { TipModal } from '../tip/TipModal';
import {
  buildCompedItemDiscountPayload,
  buildItemDiscountPayload,
  calculateOrderDiscount,
  getMaxPerUnitDiscount,
  getRemainingAfterGlobalDiscount,
  getTotalDiscountDelta,
  hasExistingItemDiscount,
  resolveGlobalDiscountScopeForPayments,
  resolveGlobalDiscountPersistence,
} from '../shared/discount.utils';
import {
  computeItemPaymentSplitAmount,
  getItemPaymentSelectionCap,
  getSelectableItemPaymentQuantity,
  hasItemPaymentSplitOptions,
  isItemPaymentSelectableLine,
} from '../shared/item-payment.utils';
import { useApprovals } from './useApprovals';
import type { ApprovalPayload, ApprovalRequestView } from './useApprovals';
import { ApprovalRequestModal } from '../approval-request-modal/ApprovalRequestModal';
import { ApprovalsModal } from '../approvals-modal/ApprovalsModal';
import { PendingApprovalBadge } from '../shared/PendingApprovalBadge';
import {
  buildCancelAllOrderlineSelections,
  buildCancelApprovalPayload,
  hasPaidPaymentline,
  isReceiptCancelableOrderline,
  resolveCancelScope,
} from '../../../core/order-cancel-flow';
import { AverageBreakdownButton } from './average-breakdown/AverageBreakdownButton/AverageBreakdownButton';
import { logToBackend } from '../../../../../services/client-log/client-log.service';
import { buildAverageBreakdownRows } from './average-breakdown/shared/average-breakdown.utils';

const EMPTY_LOCKED_PAYMENTLINE_IDS: ReadonlySet<number> = new Set();
const EMPTY_FAILED_PAYMENTLINE_IDS: ReadonlySet<number> = new Set();
const REMOVE_DISCOUNT_DISABLED_MESSAGE = 'על מנת להסיר את ההנחה יש לפנות למנהל';

// Fallback for non-secure contexts (e.g. LAN http:// access) where crypto.randomUUID is unavailable.
// Backend validates actionGroupId as a strict UUID, so the fallback must also be RFC4122-shaped.
const generateUuidV4Fallback = (): string => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  const r = (Math.random() * 16) | 0;
  const v = c === 'x' ? r : (r & 0x3) | 0x8;
  return v.toString(16);
});

// Summary: Orchestration contract for POS order/cart/payment interactions.
// Details: docs/function-library/features/category-report-group/category-report-group.md#pos-average-breakdown
type PosOrdersSectionProps = {
  tenantId: string;
  employeeSession: string | null;
  selectedOrderTableId: number | null;
  selectedOrderTableLabel: string | null;
  selectedOrderId: number | null;
  isManager: boolean;
  currentEmployeeName?: string | null;
  activeOrders: OrderView[];
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  posProducts: ProductView[];
  posCategories: CategoryView[];
  happyHour?: HappyHourView | null;
  happyHourEffective?: boolean;
  isPaymentScreenOpen: boolean;
  isReceiptScreenOpen: boolean;
  isLoadingPaymentlines: boolean;
  isPreparingPaymentline: boolean;
  isMarkingPaymentDone: boolean;
  isSplittingPayment: boolean;
  isSettingTip: boolean;
  lockedCreditPaymentlineIds?: ReadonlySet<number>;
  failedCreditPaymentlineIds?: ReadonlySet<number>;
  isLoadingPosProducts: boolean;
  isAddingOrderline: boolean;
  isAdjustingOrderline: boolean;
  isSendingToKitchen: boolean;
  isLoadingOrderlines: boolean;
  orderlinesLoadedOrderId: number | null;
  isClosingOrder: boolean;
  isOpeningOrder: boolean;
  activeSession: SessionView | null;
  productSearch: string;
  onProductSearchChange: (value: string) => void;
  recentResultIds?: number[];
  recentResultsVersion?: number;
  onClearRecentResults?: () => void;
  onOpenOrder: () => void;
  onCloseOrder: (orderId: number) => void;
  onPayAndCloseIfDone: (orderId: number, paymentlineId?: number) => void;
  onSettleCashOverage?: (
    orderId: number,
    paymentlineId: number,
    tenderedAmount: number,
    action: 'return_change' | 'add_to_tip',
  ) => Promise<boolean>;
  onCashChangeDialogOpenChange?: (open: boolean) => void;
  onRevertCashPayment: (paymentlineId: number) => Promise<boolean> | void;
  onRecoverFailedCreditPaymentline?: (orderId: number, paymentlineId: number) => Promise<boolean> | void;
  posTerminals?: PaymentTerminalView[];
  onChargeCredit?: (orderId: number, paymentlineId: number, terminalId: number, pinpadId: string) => void;
  onChargeManualCredit?: (
    orderId: number,
    paymentlineId: number,
    terminalId: number,
    pinpadId: string,
    splitTotal?: number,
    lineFullTotal?: number,
  ) => void;
  onChargeTapOnPhone?: (orderId: number, paymentlineId: number) => void;
  onSplitAndPay?: (orderId: number, paymentlineId: number, payload: SplitAndPayRequest) => Promise<boolean> | void;
  onSplitAndPayTapOnPhone?: (orderId: number, paymentlineId: number, total: number) => Promise<boolean> | void;
  onShowPartialReceipt?: (orderId: number, paymentlineId: number) => void;
  isTapOnPhoneAvailable?: boolean;
  onSplitPayment: (payload: SplitPaymentlinePayload, paymentlineId?: number) => Promise<boolean>;
  onSetOrderTip: (payload: TipPayload) => Promise<boolean> | void;
  onSetPaymentlineTip: (paymentlineId: number, payload: TipPayload) => Promise<boolean> | void;
  onResetOrderTip: () => Promise<boolean> | void;
  onAddOrderline: (orderId: number, productId: number) => void;
  onIncrementOrderline: (line: OrderlineView) => void;
  onDecrementOrderline: (line: OrderlineView) => void;
  onSetOrderlineQuantity: (line: OrderlineView, quantity: number) => void;
  onEditOrderlineSelections: (line: OrderlineView) => void;
  onUpdateOrderlineNote: (line: OrderlineView, note: string | null) => void;
  onSendToKitchen: (orderId: number, options?: { skipPrintJobs?: boolean; markSkipped?: boolean; actionGroupId?: string; optimisticSentLineIds?: number[] }) => void;
  activePrinterIds?: number[];
  onRequestKitchenFireDispatch?: (request: KitchenFireDispatchRequest) => void;
  onOpenPaymentScreen: (orderId: number, options?: { tipOnly?: boolean }) => void;
  onOpenReceiptScreen: (orderId: number) => void;
  onOpenReceiptPreview: () => void;
  onEditGuestName: () => void;
  onSaveGuestName: (orderId: number, guestName: string | null) => Promise<boolean>;
  onMenuScreen: () => void;
  onBackToAreas: () => void;
  onSelectOrderFromList: (order: OrderView) => void;
  onUpdateDiners?: (dinersCount: number) => void;
  isUpdatingDiners?: boolean;
  currentDinersCount?: number;
  onTransfer?: () => void;
  // Merged tables can't transfer (long-press rule); the button disables instead of hiding.
  isMergedTable?: boolean;
  isTransferring?: boolean;
  onToggleOrderVip?: (orderId: number, isVip: boolean) => void;
  onToggleDeposit?: (orderId: number, hasDeposit: boolean) => void;
  resolveComboSelectionRoute?: (productId: number, itemId: number) => ComboSelectionRoute | null;
  onApprovalSubmitted?: () => Promise<void> | void;
  approvalEventCallbackRef?: MutableRefObject<((request: ApprovalRequestView) => void) | null>;
  cancelAllSentLinesCallbackRef?: MutableRefObject<(() => void) | null>;
  paidActive?: boolean;
  paidOrders?: OrderView[];
  isLoadingPaid?: boolean;
  onTogglePaid?: () => void;
  onOpenPaidReceipt?: (orderId: number) => void;
  onPreviewBill?: (orderId: number) => void;
};

const PosOrdersSectionComponent: React.FC<PosOrdersSectionProps> = ({
  tenantId,
  employeeSession,
  selectedOrderTableId,
  selectedOrderTableLabel,
  selectedOrderId,
  isManager,
  currentEmployeeName = null,
  activeOrders,
  orderlines,
  paymentlines,
  posProducts,
  posCategories,
  happyHour = null,
  happyHourEffective = false,
  isPaymentScreenOpen,
  isReceiptScreenOpen,
  isLoadingPaymentlines,
  isPreparingPaymentline,
  isMarkingPaymentDone,
  isSplittingPayment,
  isSettingTip,
  lockedCreditPaymentlineIds = EMPTY_LOCKED_PAYMENTLINE_IDS,
  failedCreditPaymentlineIds = EMPTY_FAILED_PAYMENTLINE_IDS,
  isLoadingPosProducts,
  isAddingOrderline,
  isAdjustingOrderline,
  isSendingToKitchen,
  isLoadingOrderlines,
  orderlinesLoadedOrderId,
  isClosingOrder,
  isOpeningOrder,
  activeSession,
  productSearch,
  onProductSearchChange,
  recentResultIds = [],
  recentResultsVersion = 0,
  onClearRecentResults,
  onOpenOrder,
  onCloseOrder,
  onPayAndCloseIfDone,
  onSettleCashOverage,
  onCashChangeDialogOpenChange,
  onRevertCashPayment,
  onRecoverFailedCreditPaymentline,
  posTerminals = [],
  onChargeCredit,
  onChargeManualCredit,
  onChargeTapOnPhone,
  onSplitAndPay,
  onSplitAndPayTapOnPhone,
  onShowPartialReceipt,
  isTapOnPhoneAvailable,
  onSplitPayment,
  onSetOrderTip,
  onSetPaymentlineTip,
  onResetOrderTip,
  onAddOrderline,
  onIncrementOrderline,
  onDecrementOrderline,
  onSetOrderlineQuantity,
  onEditOrderlineSelections,
  onUpdateOrderlineNote,
  onSendToKitchen,
  activePrinterIds,
  onRequestKitchenFireDispatch,
  onOpenPaymentScreen,
  onOpenReceiptScreen,
  onOpenReceiptPreview,
  onEditGuestName,
  onSaveGuestName,
  onMenuScreen,
  onBackToAreas,
  onSelectOrderFromList,
  onUpdateDiners,
  isUpdatingDiners = false,
  currentDinersCount,
  onTransfer,
  isMergedTable = false,
  isTransferring = false,
  onToggleOrderVip,
  onToggleDeposit,
  resolveComboSelectionRoute,
  onApprovalSubmitted,
  approvalEventCallbackRef,
  cancelAllSentLinesCallbackRef,
  paidActive = false,
  paidOrders = [],
  isLoadingPaid = false,
  onTogglePaid,
  onOpenPaidReceipt,
  onPreviewBill,
}) => {
  const isTabletWidth = useMediaQuery('(min-width: 768px) and (max-width: 1200px)');
  const approvals = useApprovals({
    tenantId,
    employeeSession,
    activeSessionId: activeSession?.id ?? null,
    orderId: selectedOrderId,
    isManager,
    onApprovalSubmitted,
  });
  // Wire SSE callback ref so PosShellPage can forward approval events into this hook
  useEffect(() => {
    if (approvalEventCallbackRef) {
      approvalEventCallbackRef.current = approvals.onApprovalEvent;
    }
    return () => {
      if (approvalEventCallbackRef) {
        approvalEventCallbackRef.current = null;
      }
    };
  }, [approvalEventCallbackRef, approvals.onApprovalEvent]);

  useEffect(() => {
    if (!cancelAllSentLinesCallbackRef) return;
    cancelAllSentLinesCallbackRef.current = () => {
      const selections = buildCancelAllOrderlineSelections(orderlines);
      if (selections.length === 0) return;
      approvals.requestApproval('cancel', buildCancelApprovalPayload(selections, 'order'));
    };
    return () => {
      cancelAllSentLinesCallbackRef.current = null;
    };
  });

  const [quantityEditLine, setQuantityEditLine] = React.useState<OrderlineView | null>(null);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = React.useState(false);
  const [selectedSplitPaymentlineId, setSelectedSplitPaymentlineId] = React.useState<number | null>(null);
  const [tipTarget, setTipTarget] = React.useState<{ scope: 'order' } | { scope: 'line'; line: PaymentlineView } | null>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isOrderActionsOpen, setIsOrderActionsOpen] = useState(false);
  const [isGeneralItemOpen, setIsGeneralItemOpen] = useState(false);
  const [isPrintLogOpen, setIsPrintLogOpen] = useState(false);
  const [isLoadingPrintLog, setIsLoadingPrintLog] = useState(false);
  const [isReprintSelectOpen, setIsReprintSelectOpen] = useState(false);
  const [isReprinting, setIsReprinting] = useState(false);
  const [printLogs, setPrintLogs] = useState<OrderPrintLogView[]>([]);
  const [activePrintLogItems, setActivePrintLogItems] = useState<OrderPrintLogView | null>(null);
  const [isAddingGeneralItem, setIsAddingGeneralItem] = useState(false);
  const [isRefundCodeOpen, setIsRefundCodeOpen] = useState(false);
  const [refundCodeInput, setRefundCodeInput] = useState('');
  const [pendingRefundPayload, setPendingRefundPayload] = useState<
    { orderId: number; payload: GeneralItemPayload; ownerEmail?: string } | null
  >(null);
  const [isGlobalDiscountOpen, setIsGlobalDiscountOpen] = useState(false);
  const [isItemSelectOpen, setIsItemSelectOpen] = useState(false);
  const [isItemDiscountOpen, setIsItemDiscountOpen] = useState(false);
  const [isOthSelectOpen, setIsOthSelectOpen] = useState(false);
  const [isCancelSelectOpen, setIsCancelSelectOpen] = useState(false);
  const [isItemPaymentSelectOpen, setIsItemPaymentSelectOpen] = useState(false);
  const [itemSelectionSubtotal, setItemSelectionSubtotal] = useState(0);
  const [pendingItemSelections, setPendingItemSelections] = useState<OrderlineSelection[]>([]);
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);
  const [isQueueingKitchenPrint, setIsQueueingKitchenPrint] = useState(false);
  const [isFireMenuOpen, setIsFireMenuOpen] = useState(false);
  const [isUnsentPaymentPromptOpen, setIsUnsentPaymentPromptOpen] = useState(false);
  const [isResetPaymentConfirmOpen, setIsResetPaymentConfirmOpen] = useState(false);
  const [isResettingPayment, setIsResettingPayment] = useState(false);
  const [activeFireChoice, setActiveFireChoice] = useState<'green' | 'red' | null>(null);
  const [kitchenPrinterName, setKitchenPrinterName] = useState('Kitchen Printer');
  const [kitchenPrintTicketBadge, setKitchenPrintTicketBadge] = useState('חדש');
  const [kitchenPreviewOverrideLines, setKitchenPreviewOverrideLines] = useState<KdsPrintTicketLine[] | null>(null);
  const [noteEditLine, setNoteEditLine] = useState<OrderlineView | null>(null);
  const [activeFilter, setActiveFilter] = useState<OrdersFilterKey>('all');
  const [filterNowMs, setFilterNowMs] = useState(() => Date.now());
  const discountInFlightRef = useRef(false);
  const printReceiptPaperRef = useRef<HTMLDivElement>(null);
  const sendToKitchenFabRef = useRef<HTMLButtonElement | null>(null);
  const activeFireChoiceRef = useRef<'green' | 'red' | null>(null);
  activeFireChoiceRef.current = activeFireChoice;
  const fireDragStartXRef = useRef<number | null>(null);
  const fireDragChoiceRef = useRef<'green' | 'red' | null>(null);
  const [fireDragChoice, setFireDragChoice] = useState<'green' | 'red' | null>(null);
  const [fireDragOffset, setFireDragOffset] = useState(0);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const generalItemInFlightRef = useRef(false);
  const kitchenPrintInFlightRef = useRef(false);
  const autoOpenedApprovalsRef = useRef(false);

  const hasCancelableLines = buildCancelAllOrderlineSelections(orderlines).length > 0;

  // Currently-shown pending-approval toast ids — hidden on order/tab navigation (same as pressing X).
  const openPendingApprovalToastIdsRef = useRef<Set<string>>(new Set());

  const showPendingApprovalToast = () => {
    const pendingRequest = approvals.orderPendingRequests[0] ?? null;
    const toastId = pendingRequest ? `pending-approval-${pendingRequest.id}` : 'pending-approval';
    openPendingApprovalToastIdsRef.current.add(toastId);

    const handleCancelRequest = async () => {
      if (!pendingRequest) return;
      await approvals.decide(pendingRequest.id, 'denied');
      notifications.hide(toastId);
      openPendingApprovalToastIdsRef.current.delete(toastId);
    };

    notifications.show({
      id: toastId,
      color: 'orange',
      title: 'בקשת מנהל פעילה',
      autoClose: false,
      classNames: { root: orderClasses.pendingApprovalToastRoot },
      onClose: () => {
        openPendingApprovalToastIdsRef.current.delete(toastId);
      },
      message: (
        <div className={orderClasses.pendingApprovalToast}>
          <span>קיימת בקשת מנהל פתוחה להזמנה, יש להמתין לאישור או דחייה</span>
          {pendingRequest ? (
            <SharedButton variant="cancel" size="sm" onClick={handleCancelRequest} loading={approvals.isDeciding} dataTestId="pending-approval-cancel-request">
              בטל בקשה
            </SharedButton>
          ) : null}
        </div>
      ),
    });
  };

  // Leaving the current order (or the section unmounting on tab switch) dismisses any open
  // pending-approval toast, same as pressing its own X.
  useEffect(() => {
    return () => {
      openPendingApprovalToastIdsRef.current.forEach((toastId) => notifications.hide(toastId));
      openPendingApprovalToastIdsRef.current.clear();
    };
  }, [selectedOrderId]);

  const openReceiptActions = () => {
    if (!isManager && approvals.hasPendingRequest) {
      showPendingApprovalToast();
      return;
    }
    setIsActionsOpen(true);
  };

  const openOrderActions = () => {
    setIsOrderActionsOpen(true);
  };

  const openTransfer = () => {
    if (approvals.hasPendingRequest) {
      showPendingApprovalToast();
      return;
    }
    if (!hasCancelableLines) {
      notifications.show({ color: 'blue', title: 'אין פריטים', message: 'אין פריטים להעברה בשולחן זה', autoClose: 3000 });
      return;
    }
    onTransfer?.();
  };
  const printLogLoadInFlightRef = useRef(false);
  const reprintInFlightRef = useRef(false);

  useEffect(() => {
    if (!isManager || selectedOrderId !== null || approvals.allPendingCount <= 0 || autoOpenedApprovalsRef.current) {
      return;
    }
    autoOpenedApprovalsRef.current = true;
    approvals.openApprovalsModal();
  }, [approvals, isManager, selectedOrderId]);

  const LONG_PRESS_MS = 500;
  const LONG_PRESS_MOVE_CANCEL = 12;

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressStartRef.current = null;
  };

  // Long-press a cart line (away from its buttons) to open the kitchen-note modal. Movement beyond a small
  // threshold or an early pointer-up cancels it so normal taps/scrolls don't trigger the note.
  const handleLineLongPressStart = (line: OrderlineView) => (event: React.PointerEvent) => {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    clearLongPress();
    longPressStartRef.current = { x: event.clientX, y: event.clientY };
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressStartRef.current = null;
      setNoteEditLine(line);
    }, LONG_PRESS_MS);
  };

  const handleLineLongPressMove = (event: React.PointerEvent) => {
    const start = longPressStartRef.current;
    if (!start) return;
    if (
      Math.abs(event.clientX - start.x) > LONG_PRESS_MOVE_CANCEL
      || Math.abs(event.clientY - start.y) > LONG_PRESS_MOVE_CANCEL
    ) {
      clearLongPress();
    }
  };

  const handleConfirmKitchenNote = (note: string | null) => {
    if (noteEditLine) {
      onUpdateOrderlineNote(noteEditLine, note);
    }
    setNoteEditLine(null);
  };

  const submitGeneralItem = async (
    payload: GeneralItemPayload,
    refundCode?: string,
    forcedOrderId?: number,
    logAsManagerAction = true,
  ): Promise<boolean> => {
    if (!employeeSession) {
      return false;
    }
    const resolvedOrderId = forcedOrderId ?? tableOrder?.id;
    if (!resolvedOrderId) {
      return false;
    }
    const createdLine = await orderlinesService.addGeneralItem(
      tenantId,
      resolvedOrderId,
      payload.unitPrice,
      payload.quantity,
      employeeSession,
      refundCode,
    );
    if (payload.note) {
      await orderlinesService.updateNote(
        tenantId,
        resolvedOrderId,
        createdLine.id,
        payload.note,
        employeeSession,
      );
    }
    if (logAsManagerAction) {
      await recordManagerAction({ actionType: 'general_item', unitPrice: payload.unitPrice, quantity: payload.quantity });
    }
    if (payload.unitPrice < 0) {
      onOpenPaymentScreen(resolvedOrderId);
    }
    return true;
  };

  // Adds a general item, then attaches its note via the canonical updateNote path. Re-entry locked (Rule 44).
  const handleAddGeneralItem = async (payload: GeneralItemPayload) => {
    if (!tableOrder || !employeeSession || generalItemInFlightRef.current) {
      return;
    }
    // Positive items use the reason picker (backend auto-adds for managers); only a manager
    // refund (negative) stays on the direct 2FA path — the approval-execute path can't refund.
    const shouldUseApprovalFlow = payload.unitPrice >= 0;
    if (shouldUseApprovalFlow) {
      setIsGeneralItemOpen(false);
      approvals.requestApproval('general_item', { actionType: 'general_item', unitPrice: payload.unitPrice, quantity: payload.quantity });
      return;
    }
    generalItemInFlightRef.current = true;
    setIsAddingGeneralItem(true);
    try {
      await submitGeneralItem(payload, undefined, undefined, isManager);
      setIsGeneralItemOpen(false);
    } catch (err) {
      if (err instanceof Error && err.message === 'REFUND_2FA_REQUIRED') {
        setPendingRefundPayload({ orderId: tableOrder.id, payload, ownerEmail: (err as Error & { ownerEmail?: string }).ownerEmail });
        setRefundCodeInput('');
        setIsRefundCodeOpen(true);
        setIsGeneralItemOpen(false);
        return;
      }
      notifications.show({
        title: 'שגיאה בהוספת פריט כללי',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        color: 'red',
        autoClose: 3000,
      });
    } finally {
      generalItemInFlightRef.current = false;
      setIsAddingGeneralItem(false);
    }
  };

  const handleConfirmRefundCode = async () => {
    if (!pendingRefundPayload || refundCodeInput.trim().length !== 6) {
      return;
    }
    if (!tableOrder || !employeeSession || generalItemInFlightRef.current) {
      return;
    }
    generalItemInFlightRef.current = true;
    setIsAddingGeneralItem(true);
    try {
      await submitGeneralItem(
        pendingRefundPayload.payload,
        refundCodeInput.trim(),
        pendingRefundPayload.orderId,
      );
      setPendingRefundPayload(null);
      setRefundCodeInput('');
      setIsRefundCodeOpen(false);
    } catch (err) {
      notifications.show({
        title: 'שגיאה באימות קוד זיכוי',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        color: 'red',
        autoClose: 3000,
      });
    } finally {
      generalItemInFlightRef.current = false;
      setIsAddingGeneralItem(false);
    }
  };

  const appendRefundDigit = (digit: string) => {
    if (isAddingGeneralItem) {
      return;
    }
    setRefundCodeInput((previous) => `${previous}${digit}`.replace(/\D/g, '').slice(0, 6));
  };

  const backspaceRefundDigit = () => {
    if (isAddingGeneralItem) {
      return;
    }
    setRefundCodeInput((previous) => previous.slice(0, -1));
  };

  const clearRefundCode = () => {
    if (isAddingGeneralItem) {
      return;
    }
    setRefundCodeInput('');
  };
  const [isDinersNumPadOpen, setIsDinersNumPadOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  React.useEffect(() => {
    const intervalId = window.setInterval(() => setFilterNowMs(Date.now()), 30_000);
    return () => window.clearInterval(intervalId);
  }, []);

  // Summary: Product lookup maps used for limit reads during line rendering.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
  const productLimitById = React.useMemo(
    () => new Map(posProducts.map((product) => [product.id, product.limit ?? null] as const)),
    [posProducts],
  );
  const activeCategoryIds = React.useMemo(
    () => new Set(posCategories.map((category) => category.id)),
    [posCategories],
  );
  // Reset filter to 'all' when its category goes inactive (rail hides, avoid being stuck).
  React.useEffect(() => {
    if (typeof categoryFilter === 'number' && !activeCategoryIds.has(categoryFilter)) {
      setCategoryFilter('all');
    }
  }, [activeCategoryIds, categoryFilter]);

  const tableOrder = activeOrders.find((entry) => entry.id === selectedOrderId);
  // Track only a seen SENT line; removed pending lines are deleted so it reverts to tip-only.
  const orderIdsWithSentLinesRef = useRef<Set<number>>(new Set());
  if (tableOrder && orderlines.some((line) => line.status !== 'pending')) {
    orderIdsWithSentLinesRef.current.add(tableOrder.id);
  }
  const [isDepositReturnOpen, setIsDepositReturnOpen] = useState(false);
  const wasPaymentScreenOpenRef = useRef(false);
  useEffect(() => {
    const entered = isPaymentScreenOpen && !wasPaymentScreenOpenRef.current;
    wasPaymentScreenOpenRef.current = isPaymentScreenOpen;
    if (entered && tableOrder?.hasDeposit) {
      setIsDepositReturnOpen(true);
    }
  }, [isPaymentScreenOpen, tableOrder?.hasDeposit]);
  // Take-away bills its unsent (pending) lines too (paid upfront, sent to kitchen at close).
  const isTakeAway = tableOrder?.orderType === 'take_away';
  // Quick-charge (QC_ code): only the payment screen or the ביטול-only bill, never the menu.
  const isQuickChargeOrder = isQuickChargeOrderCode(tableOrder?.orderCode);
  // Central render guard: force payment view for QC whenever the bill isn't open (never menu).
  const showPaymentView = isPaymentScreenOpen || (isQuickChargeOrder && !isReceiptScreenOpen);
  const cartTotal = orderlines
    .filter((line) => line.status !== 'pending' || isTakeAway)
    .reduce((sum, line) => sum + line.unitPrice * line.quantity - (line.discountInput ?? 0), 0);

  const paidAmount = paymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce((sum, line) => sum + line.amount, 0);

  // Summary: Order-level payment math and discount projections for screen sections.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection
  // Discount ceiling excludes tips by design - discounts apply to the bill, never the tip.
  const remainingToPayMinusTips = cartTotal - paidAmount;

  const discount = calculateOrderDiscount(cartTotal, tableOrder?.discountMode, tableOrder?.discountInput);
  const billableCartTotal = cartTotal - discount;
  const remainingAfterGlobalDiscount = getRemainingAfterGlobalDiscount(remainingToPayMinusTips, discount);
  // Tip-only needs an item-less order: a live line, history flag, or seen sent line disqualifies.
  const hasOrderlineHistory = tableOrder?.hasOrderlineHistory === true
    || orderlines.length > 0
    || (tableOrder != null && orderIdsWithSentLinesRef.current.has(tableOrder.id));
  // Canceled and zero-quantity rows are intentionally absent from `orderlines`: this controls
  // the empty-cart/release-table UI and must not be conflated with tip-only eligibility.
  const hasVisibleOrderlines = orderlines.length > 0;
  // Empty QC is tip-only (base 0): use per-line tip; global alloc over a zero base pays 0.
  const isTipOnlyOrder = !hasOrderlineHistory && (!isTakeAway || isQuickChargeOrder);

  const cartListRef = useRef<HTMLUListElement | null>(null);
  const prevCartLineIdsRef = useRef<Set<number>>(new Set());
  const scrolledCartOrderIdRef = useRef<number | null>(null);
  // Auto-scroll to a freshly appended line (orderline.added, incl. cross-device), regardless of
  // current scroll position. Order switch seeds silently; qty/note updates never scroll.
  useEffect(() => {
    const list = cartListRef.current;
    const currentIds = new Set(orderlines.map((line) => line.id));
    if (scrolledCartOrderIdRef.current !== selectedOrderId) {
      scrolledCartOrderIdRef.current = selectedOrderId;
      prevCartLineIdsRef.current = currentIds;
      return;
    }
    const wasEmpty = prevCartLineIdsRef.current.size === 0;
    const hasNewLine = orderlines.some((line) => !prevCartLineIdsRef.current.has(line.id));
    prevCartLineIdsRef.current = currentIds;
    if (!list || !hasNewLine || wasEmpty) {
      return;
    }
    list.scrollTop = list.scrollHeight;
  }, [orderlines, selectedOrderId]);
  const hasSentOrderlines = orderlines.some((line) => line.status !== 'pending' || isTakeAway);
  const hasPendingLines = orderlines.some((line) => line.status === 'pending');
  const pendingOrderlines = orderlines.filter((line) => line.status === 'pending' && line.quantity > 0);
  const activePaymentline = paymentlines.find((line) => line.status === 'new') ?? paymentlines[0] ?? null;
  const tableLabel = selectedOrderTableLabel ?? tableOrder?.tableNumber ?? tableOrder?.orderCode ?? '';
  const dinersCount = Number(tableOrder?.dinersCount) || 1;
  const tableAverage = formatMoney(Number(tableOrder?.totalAmount ?? 0) / dinersCount);
  const pendingApprovalOrderlineIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const request of approvals.orderPendingRequests) {
      switch (request.payload.actionType) {
        case 'item_discount':
        case 'on_the_house':
        case 'cancel':
          request.payload.items.forEach((item) => ids.add(item.orderlineId));
          break;
        default:
          break;
      }
    }
    return ids;
  }, [approvals.orderPendingRequests]);

  const hasPendingGlobalDiscount = approvals.orderPendingRequests.some(
    (r) => r.payload.actionType === 'global_discount',
  );
  const hasPendingGeneralItem = approvals.orderPendingRequests.some(
    (r) => r.payload.actionType === 'general_item',
  );
  const [isAverageBreakdownOpen, setIsAverageBreakdownOpen] = useState(false);
  const averageBreakdownRef = useRef<HTMLButtonElement>(null);
  const categoryRail = buildCategoryRail(posCategories);
  const { rails: categoryChildRails, activeIds: selectedAncestorIds } = buildAncestorRails(posCategories, categoryFilter);
  const displayCategoryChildRails = useMemo(() => {
    if (categoryChildRails.length < 2) {
      return categoryChildRails.map((rail) => rail.map((item) => ({ ...item, nested: false })));
    }

    const byId = new Map(posCategories.map((category) => [category.id, category]));
    const firstRail = categoryChildRails[0] ?? [];
    const secondRail = categoryChildRails[1] ?? [];

    let anchorParentId: number | null = null;
    if (typeof categoryFilter === 'number') {
      const selected = byId.get(categoryFilter);
      if (selected) {
        const selectedHasChildren = posCategories.some(
          (category) => category.isActive && category.parentCategoryPk === selected.id,
        );
        anchorParentId = selectedHasChildren ? selected.id : selected.parentCategoryPk;
      }
    }

    const merged = firstRail.flatMap((item) => {
      const base = [{ ...item, nested: false }];
      if (anchorParentId !== null && item.filter === anchorParentId) {
        return [
          ...base,
          ...secondRail.map((child) => ({ ...child, nested: true })),
        ];
      }
      return base;
    });

    if (anchorParentId === null || merged.length === firstRail.length) {
      return [firstRail.map((item) => ({ ...item, nested: false }))];
    }

    return [merged];
  }, [categoryChildRails, categoryFilter, posCategories]);

  const childRailColumnsForLayout = displayCategoryChildRails.length;
  const productGroups = buildMenuProductGroups(posProducts, posCategories, categoryFilter, productSearch, recentResultIds);
  // On snapshot capture, navigate to "all" so the חיפוש אחרון separator group shows on top.
  useEffect(() => {
    if (recentResultsVersion > 0) {
      setCategoryFilter('all');
    }
  }, [recentResultsVersion]);
  // Leaving the section (order switch/close/unmount) drops the snapshot group too.
  useEffect(() => () => onClearRecentResults?.(), [onClearRecentResults]);
  // Tapping any category or focusing the search box drops the snapshot group.
  const handleCategorySelect = (filter: CategoryFilter) => {
    setCategoryFilter(filter);
    onClearRecentResults?.();
  };
  const handleSearchFocus = () => onClearRecentResults?.();
  const hasMatchingProducts = productGroups.some((group) => group.products.length > 0);
  const averageBreakdownRows = useMemo(
    () => buildAverageBreakdownRows(orderlines, posProducts, posCategories, isTakeAway),
    [isTakeAway, orderlines, posCategories, posProducts],
  );

  useEffect(() => {
    setIsAverageBreakdownOpen(false);
  }, [selectedOrderId]);

  useEffect(() => {
    if (!isAverageBreakdownOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!averageBreakdownRef.current?.contains(event.target as Node)) {
        setIsAverageBreakdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);

    const autoCloseTimer = setTimeout(() => setIsAverageBreakdownOpen(false), 5000);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      clearTimeout(autoCloseTimer);
    };
  }, [isAverageBreakdownOpen]);

  const { resolvedGlobalTip, visiblePaymentlines, openLineCount, openTipShareById, hasOpenLineTips } =
    derivePaymentScreenTip(tableOrder, billableCartTotal, paymentlines);

  // Amount keypad (Odoo partial tender): every method tap opens it prefilled with the amount-due.
  const [amountEntryTarget, setAmountEntryTarget] = useState<
    { orderId: number; paymentlineId: number; lineFullTotal: number; method: PaymentMethodTap; methodLabel: string } | null
  >(null);
  const [cashChangeTarget, setCashChangeTarget] = useState<
    { orderId: number; paymentlineId: number; tenderedAmount: number; dueAmount: number } | null
  >(null);

  const handleMethodTap = useCallback(
    (paymentline: PaymentlineView, method: PaymentMethodTap, methodLabel: string) => {
      if (!tableOrder) {
        return;
      }
      const lineTip = resolveTip(paymentline.tipInputMode ?? null, paymentline.tipInputValue ?? null, paymentline.amount);
      const globalShare = openTipShareById.get(paymentline.id) ?? 0;
      const lineFullTotal = roundMoney(paymentline.amount + lineTip + globalShare);
      if (lineFullTotal < 0) {
        if (method.kind === 'cash') {
          onPayAndCloseIfDone(tableOrder.id, paymentline.id);
        } else if (method.kind === 'pinpad') {
          onChargeCredit?.(tableOrder.id, paymentline.id, method.terminalId, method.pinpadId);
        }
        return;
      }
      setAmountEntryTarget({ orderId: tableOrder.id, paymentlineId: paymentline.id, lineFullTotal, method, methodLabel });
    },
    [onChargeCredit, onPayAndCloseIfDone, openTipShareById, tableOrder],
  );

  const handleAmountEntryConfirm = useCallback(
    (total: number) => {
      const target = amountEntryTarget;
      if (!target) {
        return;
      }
      setAmountEntryTarget(null);
      const { orderId, paymentlineId, method, lineFullTotal } = target;
      const isFull = roundMoney(total) >= roundMoney(lineFullTotal);
      if (method.kind === 'cash') {
        if (roundMoney(total) > roundMoney(lineFullTotal)) {
          setCashChangeTarget({ orderId, paymentlineId, tenderedAmount: total, dueAmount: lineFullTotal });
        } else if (isFull) {
          onPayAndCloseIfDone(orderId, paymentlineId);
        } else {
          void onSplitAndPay?.(orderId, paymentlineId, { tender: 'cash', amount: total });
        }
        return;
      }
      if (method.kind === 'pinpad') {
        if (isFull) {
          onChargeCredit?.(orderId, paymentlineId, method.terminalId, method.pinpadId);
        } else {
          void onSplitAndPay?.(orderId, paymentlineId, {
            tender: 'pinpad',
            amount: total,
            terminalId: method.terminalId,
            pinpadId: method.pinpadId,
          });
        }
        return;
      }
      if (method.kind === 'manual') {
        // Card entry is in the manual modal; a partial total threads through (undefined = full).
        onChargeManualCredit?.(
          orderId,
          paymentlineId,
          method.terminalId,
          method.pinpadId,
          isFull ? undefined : total,
          lineFullTotal,
        );
        return;
      }
      if (isFull) {
        onChargeTapOnPhone?.(orderId, paymentlineId);
      } else {
        void onSplitAndPayTapOnPhone?.(orderId, paymentlineId, total);
      }
    },
    [amountEntryTarget, onChargeCredit, onChargeManualCredit, onChargeTapOnPhone, onPayAndCloseIfDone, onSplitAndPay, onSplitAndPayTapOnPhone],
  );
  const handleCashChangeDecision = useCallback(async (action: 'return_change' | 'add_to_tip') => {
    const target = cashChangeTarget;
    if (!target || !onSettleCashOverage) {
      return;
    }
    const settled = await onSettleCashOverage(
      target.orderId, target.paymentlineId, target.tenderedAmount, action,
    );
    if (settled) {
      setCashChangeTarget(null);
    }
  }, [cashChangeTarget, onSettleCashOverage]);
  // Report the change dialog open-state up for the safe-zone; reset on unmount so it can't stick.
  useEffect(() => {
    onCashChangeDialogOpenChange?.(cashChangeTarget !== null);
    return () => onCashChangeDialogOpenChange?.(false);
  }, [cashChangeTarget, onCashChangeDialogOpenChange]);
  const selectedSplitPaymentline =
    visiblePaymentlines.find((line) => line.id === selectedSplitPaymentlineId && line.status === 'new')
    ?? activePaymentline;
  const paymentAmount = selectedSplitPaymentline?.amount ?? 0;
  const printLogTitleTarget = tableOrder?.tableNumber
    ? `שולחן ${tableOrder.tableNumber}`
    : (tableOrder?.orderCode ?? '/');
  // Split preview uses the line with-tip total (global share + own tip) for equal/custom splits.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection
  const selectedSplitLineTip = selectedSplitPaymentline
    ? (openTipShareById.get(selectedSplitPaymentline.id) ?? 0)
    + resolveTip(selectedSplitPaymentline.tipInputMode ?? null, selectedSplitPaymentline.tipInputValue ?? null, paymentAmount)
    : 0;
  const splitGrandTotal = paymentAmount + selectedSplitLineTip;
  // Exclude fully-comped (net 0) lines — nothing to pay per item on an OTH'd line.
  const itemPaymentLines = orderlines.filter(isItemPaymentSelectableLine);
  const canOpenByProductSplit = hasItemPaymentSplitOptions(itemPaymentLines, paymentAmount);
  // An empty-order tip belongs to its ₪0 payment line; a global tip has no billable base to allocate.
  const isGlobalTipEnabled = visiblePaymentlines.length <= 1 && !isTipOnlyOrder;
  const isPaymentlineLocked = (line: PaymentlineView): boolean =>
    lockedCreditPaymentlineIds.has(line.id) || line.hasPendingCreditCharge === true;
  // A credit charge is mid-flight somewhere on this order: freeze the אפס reset so nobody wipes a line being charged.
  const hasLockedPaymentline = visiblePaymentlines.some(isPaymentlineLocked);
  const hasFailedPaymentline = visiblePaymentlines.some((line) => line.status === 'new' && failedCreditPaymentlineIds.has(line.id));

  const recordManagerAction = async (payload: ApprovalPayload): Promise<void> => {
    try {
      await approvals.logManagerAction(payload);
    } catch (err) {
      notifications.show({
        title: 'הפעולה נשמרה, אך הרישום ללוג נכשל',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        color: 'orange',
        autoClose: 3000,
      });
    }
  };

  // Summary: Equal-split action handler with user-facing confirmation feedback.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection
  const handleConfirmEqualSplit = async (count: number, amounts: number[]) => {
    if (!selectedSplitPaymentlineId) {
      return;
    }
    const ok = await onSplitPayment({ mode: 'equal', count }, selectedSplitPaymentlineId);
    if (ok) {
      const minAmount = Math.min(...amounts);
      const maxAmount = Math.max(...amounts);
      const message = minAmount === maxAmount
        ? `חלוקה ל-${count} תשלומים של ${formatMoney(minAmount)} ₪`
        : `חלוקה ל-${count} תשלומים בטווח ${formatMoney(minAmount)}-${formatMoney(maxAmount)} ₪`;
      notifications.show({
        color: 'green',
        title: 'חלוקה בוצעה',
        message,
        autoClose: 3000,
      });
      setIsSplitDialogOpen(false);
      setSelectedSplitPaymentlineId(null);
    }
  };

  // Summary: Custom-split action handler converting with-tip input into bill portion.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection
  const handleConfirmCustomSplit = async (customSplitAmount: number, customSplitRemainder: number) => {
    if (!selectedSplitPaymentlineId) {
      return;
    }
    // The user types a WITH-TIP amount; the backend splits the bill, so convert grand -> bill portion (tip is proportional).
    const billPortion = splitGrandTotal > 0
      ? roundMoney((customSplitAmount * paymentAmount) / splitGrandTotal)
      : customSplitAmount;
    const ok = await onSplitPayment({ mode: 'custom', amount: billPortion }, selectedSplitPaymentlineId);
    if (ok) {
      notifications.show({
        color: 'green',
        title: 'חלוקה בוצעה',
        message: `${formatMoney(customSplitAmount)} ₪ + ${formatMoney(customSplitRemainder)} ₪`,
        autoClose: 3000,
      });
      setIsSplitDialogOpen(false);
      setSelectedSplitPaymentlineId(null);
    }
  };

  const handleOpenByProductSplit = () => {
    if (!selectedSplitPaymentlineId) {
      return;
    }
    if (!canOpenByProductSplit) {
      notifications.show({
        color: 'orange',
        title: 'חלוקה לפי פריטים',
        message: 'אין פריטים זמינים לפיצול בשורת תשלום זו.',
        autoClose: 3000,
      });
      return;
    }
    setIsSplitDialogOpen(false);
    setIsItemPaymentSelectOpen(true);
  };

  const handleConfirmByProductSplit = async (selections: OrderlineSelection[], subtotal: number) => {
    if (!selectedSplitPaymentlineId) {
      return;
    }
    const items = selections.map((selection) => ({
      orderlineId: selection.orderlineId,
      quantity: selection.quantity,
    }));
    const ok = await onSplitPayment({ mode: 'by_product', items }, selectedSplitPaymentlineId);
    if (ok) {
      notifications.show({
        color: 'green',
        title: 'חלוקה בוצעה',
        message: `פוצלו ${items.length} פריטים בסך ${formatMoney(subtotal)} ₪`,
        autoClose: 3000,
      });
      setIsItemPaymentSelectOpen(false);
      setSelectedSplitPaymentlineId(null);
    }
  };

  const fallbackKitchenPreviewOrderNumber = String(tableOrder?.orderCode ?? '')
    .split('/')
    .slice(-2)
    .map((part) => part.match(/\d+/)?.[0] ?? part)
    .join('/');

  const effectiveKitchenPreviewLines: KdsPrintTicketLine[] = pendingOrderlines.flatMap((line) => {
    const { comboItems } = splitOrderlineSelections(line, resolveComboSelectionRoute);
    const lineNote = line.note && line.note.trim().length > 0 ? line.note.trim() : null;
    if (comboItems.length === 0) {
      return [{
        id: line.id,
        productName: orderlineDisplayName(line),
        selectionGroups: groupOrderlineSelections(line.selections),
        quantity: line.quantity,
        note: lineNote,
      }];
    }

    return comboItems.map((item, index) => ({
      id: line.id * 1000 + index + 1,
      productName: item.name.replace(/\sx\d+$/i, ''),
      selectionGroups: [],
      quantity: line.quantity * Math.max(1, item.quantity),
      note: index === 0 ? lineNote : null,
    }));
  });

  const printTicketLabels = {
    newBadge: kitchenPrintTicketBadge,
    orderNumber: 'מספר הזמנה',
    time: 'שעה',
    waiter: 'מלצר',
    diners: 'סועדים',
    placeholder: '---',
    table: 'שולחן',
    diningType: '',
  };

  const handlePaymentEntry = () => {
    if (approvals.hasPendingRequest) {
      showPendingApprovalToast();
      return;
    }
    if (!tableOrder) {
      return;
    }
    if (!isTakeAway && hasPendingLines) {
      setIsUnsentPaymentPromptOpen(true);
      return;
    }
    onOpenPaymentScreen(tableOrder.id);
  };

  const handlePrintKitchenDirect = async () => {
    if (!tableOrder || kitchenPrintInFlightRef.current) {
      return;
    }

    if (!employeeSession) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'לא נמצא עובד מחובר', autoClose: 3000 });
      return;
    }

    kitchenPrintInFlightRef.current = true;
    setIsQueueingKitchenPrint(true);

    // Fire-time truth fetch: guards against silently-dead SSE (M3-class) serving a stale list.
    let effectiveActivePrinterIds = activePrinterIds;
    try {
      effectiveActivePrinterIds = (await printersService.listActive(tenantId, employeeSession, { bypassCache: true })).map((printer) => printer.id);
    } catch {
      // Network hiccup: fall back to the last known snapshot rather than block all printing.
    }

    const { groups, deliverableCount, deliverableLineIds, undeliverableNames, kdsBonMissingNames, unroutedComboWarning } = buildKitchenPrintGroups({
      lines: pendingOrderlines,
      posProducts,
      resolveComboSelectionRoute,
      activePrinterIds: effectiveActivePrinterIds,
    });

    if (kdsBonMissingNames.length > 0) {
      const names = kdsBonMissingNames.join(', ');
      notifications.show({
        color: 'orange',
        title: 'הבון לא הודפס',
        message: `${names} — נשלח למסך המטבח אך הבון לא הודפס (מדפסת לא מחוברת).`,
        autoClose: 3000,
      });
    }

    if (unroutedComboWarning) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לשלוח הדפסה',
        message: `${unroutedComboWarning.fatherName} מוצר קומבו\nלפריט ${unroutedComboWarning.childName} - לא נבחרה מדפסת/מסך מטבח`,
        autoClose: 3000,
      });
    }

    if (undeliverableNames.length > 0) {
      const names = undeliverableNames.join(', ');
      const suffix = deliverableCount > 0 ? ' שאר הפריטים נשלחו.' : '';
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לשלוח להדפסה',
        message: `${names} — לא נבחרה מדפסת/מסך מטבח.${suffix}`,
        autoClose: 3000,
      });
    }

    if (deliverableCount === 0) {
      kitchenPrintInFlightRef.current = false;
      setIsQueueingKitchenPrint(false);
      return;
    }

    const releaseKitchenPrintGuard = () => {
      kitchenPrintInFlightRef.current = false;
      setIsQueueingKitchenPrint(false);
    };

    try {
      const fireActionGroupId = globalThis.crypto?.randomUUID?.() ?? generateUuidV4Fallback();
      await Promise.resolve(onSendToKitchen(tableOrder.id, { skipPrintJobs: true, actionGroupId: fireActionGroupId, optimisticSentLineIds: deliverableLineIds }));

      if (groups.length === 0) {
        logToBackend('send-to-kitchen', 'kds-only routing, no printer groups', {
          orderId: tableOrder.id,
          actionGroupId: fireActionGroupId,
          pendingLineCount: pendingOrderlines.length,
          deliverableCount,
          undeliverableNames,
          unroutedComboWarning,
        }, 'warn');
        releaseKitchenPrintGuard();
      } else {
        const dispatchCompletion = onRequestKitchenFireDispatch?.({
          order: tableOrder,
          groups,
          actionGroupId: fireActionGroupId,
          ticket: {
            tableNumber: tableOrder.tableNumber ?? tableOrder.orderCode,
            orderNumber: fallbackKitchenPreviewOrderNumber,
            dinersCount,
            waiterName: currentEmployeeName ?? tableOrder.openedByName ?? undefined,
            badge: kitchenPrintTicketBadge,
          },
        });
        // Hold the re-tap guard until background capture+queue settles; navigation stays instant.
        void Promise.resolve(dispatchCompletion).then(releaseKitchenPrintGuard, releaseKitchenPrintGuard);
      }
      onBackToAreas();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed queueing print job';
      notifications.show({ color: 'red', title: 'שגיאה', message, autoClose: 3000 });
      releaseKitchenPrintGuard();
    }
  };

  const handleSendKitchenNoPrint = async () => {
    if (!tableOrder || !employeeSession) {
      return;
    }
    try {
      await Promise.resolve(onSendToKitchen(tableOrder.id, { markSkipped: true }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed sending to kitchen';
      notifications.show({ color: 'red', title: 'שגיאה', message, autoClose: 3000 });
    }
  };

  const handleOpenPrintLog = async () => {
    if (!tableOrder || !employeeSession || printLogLoadInFlightRef.current) {
      return;
    }

    printLogLoadInFlightRef.current = true;
    setIsLoadingPrintLog(true);
    try {
      const logs = await ordersService.listPrintLogs(tenantId, tableOrder.id, employeeSession);
      setPrintLogs(logs);
      setIsPrintLogOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed loading print logs';
      notifications.show({ color: 'red', title: 'שגיאה', message, autoClose: 3000 });
    } finally {
      printLogLoadInFlightRef.current = false;
      setIsLoadingPrintLog(false);
    }
  };

  const handleConfirmReprint = async (selections: OrderlineSelection[]) => {
    if (!tableOrder || !employeeSession || !isManager || reprintInFlightRef.current) {
      return;
    }
    reprintInFlightRef.current = true;
    setIsReprinting(true);
    try {
      const actionGroupId = globalThis.crypto?.randomUUID?.() ?? generateUuidV4Fallback();
      const { printerGroups } = await orderlinesService.reprint(tenantId, tableOrder.id, employeeSession, selections, actionGroupId, true);
      setIsReprintSelectOpen(false);

      setKitchenPrintTicketBadge('הדפסה חוזרת');

      for (const group of printerGroups) {
        const groupLines: KdsPrintTicketLine[] = group.lines.map((l) => ({
          id: l.orderlineId,
          productName: l.productName,
          selectionGroups: [],
          quantity: l.quantity,
          note: null,
        }));

        setKitchenPrinterName(group.printerName);
        setKitchenPreviewOverrideLines(groupLines);

        const printableImage = process.env.NODE_ENV === 'test'
          ? 'data:image/jpeg;base64,test'
          : await captureKdsReceiptSnapshot(printReceiptPaperRef.current);
        if (!printableImage) {
          notifications.show({ color: 'red', title: 'שגיאה', message: `לא ניתן ליצור תצוגת הדפסה עבור ${group.printerName}. נסה שוב.`, autoClose: 3000 });
          continue;
        }

        await kdsService.queueOrderPrint(
          tenantId,
          tableOrder.id,
          group.printerId,
          employeeSession,
          undefined,
          group.lines.map((l) => l.orderlineId),
          actionGroupId,
          group.printerName,
          group.lines.map((l) => ({ name: l.productName, quantity: l.quantity, routeLabel: group.printerName })),
          printableImage,
          'reprint',
        );
      }

      notifications.show({
        color: 'green',
        title: 'הדפסה חוזרת נשלחה',
        message: 'הפריטים שנבחרו נשלחו שוב.',
        autoClose: 3000,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed reprinting orderlines';
      notifications.show({ color: 'red', title: 'שגיאה', message, autoClose: 3000 });
    } finally {
      setKitchenPrintTicketBadge('חדש');
      setKitchenPreviewOverrideLines(null);
      reprintInFlightRef.current = false;
      setIsReprinting(false);
    }
  };

  const closeFireMenu = () => {
    setActiveFireChoice(null);
    setFireDragOffset(0);
    setFireDragChoice(null);
    fireDragStartXRef.current = null;
    fireDragChoiceRef.current = null;
    setIsFireMenuOpen(false);
  };

  const openFireMenu = () => {
    if (isSendActionDisabled) {
      return;
    }
    setActiveFireChoice(null);
    setFireDragOffset(0);
    setFireDragChoice(null);
    fireDragStartXRef.current = null;
    fireDragChoiceRef.current = null;
    setIsFireMenuOpen(true);
  };

  const executeFireChoice = (choice: 'green' | 'red') => {
    closeFireMenu();
    if (choice === 'green') {
      void handlePrintKitchenDirect();
      return;
    }
    void handleSendKitchenNoPrint();
  };

  // Swipe-to-complete: drag a circle to the LEFT past the trigger to fire that action.
  const FIRE_SWIPE_TRIGGER = 96;
  const FIRE_SWIPE_MAX = 132;

  const handleFireSwipePointerDown = (choice: 'green' | 'red') => (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    fireDragStartXRef.current = event.clientX;
    fireDragChoiceRef.current = choice;
    setFireDragChoice(choice);
    setFireDragOffset(0);
    setActiveFireChoice(null);
  };

  const handleFireSwipePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (fireDragStartXRef.current === null) {
      return;
    }
    const rawDelta = event.clientX - fireDragStartXRef.current;
    const clamped = Math.max(-FIRE_SWIPE_MAX, Math.min(0, rawDelta));
    setFireDragOffset(clamped);
    if (clamped <= -FIRE_SWIPE_TRIGGER) {
      setActiveFireChoice(fireDragChoiceRef.current);
    } else {
      setActiveFireChoice(null);
    }
  };

  const handleFireSwipePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (fireDragStartXRef.current === null) {
      return;
    }
    fireDragStartXRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const triggered = activeFireChoiceRef.current;
    const choice = fireDragChoiceRef.current;
    fireDragChoiceRef.current = null;
    setFireDragChoice(null);
    setFireDragOffset(0);
    if (triggered && triggered === choice) {
      executeFireChoice(choice);
    }
  };

  // Take-away has no manual fire — items go to the kitchen automatically at payment close.
  const isSendActionDisabled = isSendingToKitchen || isQueueingKitchenPrint || !hasPendingLines || isTakeAway;
  const effectiveFilter = isManager ? activeFilter : 'all';
  const filterCounts = buildOrdersFilterCounts(activeOrders, filterNowMs);
  const visibleOrders = filterOrders(activeOrders, effectiveFilter, filterNowMs);

  if (selectedOrderId === null) {
    const listOrders = paidActive ? paidOrders : visibleOrders;
    return (
      <section className={orderClasses.screen}>
        <OrdersTable
          orders={listOrders}
          selectedOrderId={null}
          onSelectOrder={onSelectOrderFromList}
          paidActive={paidActive}
          onTogglePaid={onTogglePaid}
          onPreviewBill={paidActive ? undefined : onPreviewBill}
          onPreviewReceipt={paidActive ? (orderId) => onOpenPaidReceipt?.(orderId) : undefined}
          isLoadingPaid={paidActive && isLoadingPaid}
          emptyMessage={paidActive ? 'אין הזמנות ששולמו במשמרת' : undefined}
          filterSlot={isManager && !paidActive ? (
            <OrdersFilterCards
              active={activeFilter}
              counts={{ ...filterCounts, approvals: approvals.allPendingCount }}
              onSelect={(key) => {
                if (key === 'approvals') {
                  approvals.openApprovalsModal();
                  return;
                }
                setActiveFilter(key);
              }}
            />
          ) : null}
        />
        {isManager && (
          <ApprovalsModal
            opened={approvals.approvalsModalOpened}
            onClose={approvals.closeApprovalsModal}
            requests={approvals.pendingRequests.length > 0 ? approvals.pendingRequests : approvals.historyRequests}
            mode={approvals.pendingRequests.length > 0 ? 'pending' : 'history'}
            historyFilter={approvals.approvalHistoryFilter}
            onHistoryFilterChange={approvals.setApprovalHistoryFilter}
            isLoading={approvals.isLoadingHistory}
            isDeciding={approvals.isDeciding}
            hasMoreHistory={approvals.hasMoreHistory}
            onLoadMoreHistory={approvals.loadMoreHistory}
            onDecide={approvals.decide}
          />
        )}
        {approvals.approvalRequestModalState && (
          <ApprovalRequestModal
            opened={approvals.approvalRequestModalState.opened}
            actionType={approvals.approvalRequestModalState.actionType}
            payload={approvals.approvalRequestModalState.payload}
            orderlines={orderlines}
            isSubmitting={approvals.isSubmittingApproval}
            onClose={approvals.closeApprovalRequestModal}
            onSubmit={approvals.submitApproval}
          />
        )}
      </section>
    );
  }

  return (
    <>
      {/* Summary: Receipt top bar shown only in receipt mode.
          Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection */}
      {isReceiptScreenOpen && tableOrder ? (
        <ReceiptTopBar
          guestName={tableOrder.guestName ?? null}
          tableLabel={tableOrder.tableNumber ?? tableOrder.orderCode}
          isManager={isManager}
          onPrint={onOpenReceiptPreview}
          isDiscountInFlight={isSavingDiscount}
          onActions={openReceiptActions}
          onEditGuestName={onEditGuestName}
          isQuickCharge={isQuickChargeOrder}
        />
      ) : null}
      {tableOrder && showPaymentView ? (
        /* Summary: Payment tip banner with reset and global-tip entry actions.
           Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection */
        <div
          className={`${orderClasses.tipBanner} ${isGlobalTipEnabled ? '' : orderClasses.tipBannerResetOnly}`}
          data-testid="tip-banner"
        >
          <UnstyledButton
            component="button"
            type="button"
            className={orderClasses.tipBannerReset}
            data-testid="tip-banner-reset"
            disabled={hasFailedPaymentline || isSettingTip || hasLockedPaymentline || (resolvedGlobalTip <= 0 && openLineCount <= 1 && !hasOpenLineTips)}
            onClick={() => setIsResetPaymentConfirmOpen(true)}
            aria-label="אפס טיפ ושורות"
          >
            <FontAwesomeIcon icon={faRotateLeft as IconProp} />
            <span>אפס</span>
          </UnstyledButton>
          {isGlobalTipEnabled && (
            <UnstyledButton
              component="button"
              type="button"
              className={orderClasses.tipBannerMain}
              data-testid="tip-banner-open"
              disabled={isSettingTip}
              onClick={() => setTipTarget({ scope: 'order' })}
            >
              <span className={orderClasses.tipBannerValue}>{formatMoney(resolvedGlobalTip)} ₪</span>
              <span className={orderClasses.tipBannerLabel}>טיפ כללי</span>
            </UnstyledButton>
          )}
        </div>
      ) : null}
      <section className={orderClasses.screen}>
        {tableOrder ? (
          showPaymentView ? (
            /* Summary: Payment mode layout with paymentline action rows and totals footer.
               Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection */
            <>
              <div className={orderClasses.paymentScreen} data-testid="payment-screen">
                <section className={orderClasses.paymentPanel}>
                  {(isLoadingPaymentlines || isPreparingPaymentline) && (
                    <p className={orderClasses.cartEmpty}>טוען פרטי תשלום...</p>
                  )}
                  {!isLoadingPaymentlines && !isPreparingPaymentline && visiblePaymentlines.length === 0 && (
                    <p className={orderClasses.cartEmpty}>לא נמצאה שורת תשלום.</p>
                  )}
                  {!isLoadingPaymentlines &&
                    !isPreparingPaymentline &&
                    visiblePaymentlines.map((paymentline) => (
                      <PaymentActionsRow
                        key={paymentline.id}
                        paymentline={paymentline}
                        globalTipShare={
                          paymentline.status === 'done'
                            ? paymentline.globalTipCollected ?? 0
                            : openTipShareById.get(paymentline.id) ?? 0
                        }
                        isMarkingPaymentDone={isMarkingPaymentDone}
                        isSplittingPayment={isSplittingPayment}
                        isSettingTip={isSettingTip}
                        isLocked={isPaymentlineLocked(paymentline)}
                        isFailed={paymentline.status === 'new' && failedCreditPaymentlineIds.has(paymentline.id)}
                        terminals={posTerminals}
                        isTapOnPhoneAvailable={isTapOnPhoneAvailable}
                        showPartialReceipt={paymentlines.length > 1 && paymentline.status === 'done'}
                        onPartialReceipt={() => onShowPartialReceipt?.(tableOrder.id, paymentline.id)}
                        onCancelPayment={() => onRevertCashPayment(paymentline.id)}
                        onClearFailure={() => onRecoverFailedCreditPaymentline?.(tableOrder.id, paymentline.id)}
                        onMethodTap={(method) => {
                          handleMethodTap(paymentline, method, tenderKindLabel(method.kind));
                        }}
                        onTip={() => setTipTarget(isGlobalTipEnabled ? { scope: 'order' } : { scope: 'line', line: paymentline })}
                        onSplit={() => {
                          setSelectedSplitPaymentlineId(paymentline.id);
                          setIsSplitDialogOpen(true);
                        }}
                      />
                    ))}
                </section>
              </div>
              {!isLoadingPaymentlines && !isPreparingPaymentline && (
                <div className={orderClasses.paymentSummaryFooter}>
                  {visiblePaymentlines.length === 1 && visiblePaymentlines[0].status === 'new' && (
                    <QuickPaymentMethods
                      paymentline={visiblePaymentlines[0]}
                      isMarkingPaymentDone={isMarkingPaymentDone}
                      isSplittingPayment={isSplittingPayment}
                      isSettingTip={isSettingTip}
                      isLocked={isPaymentlineLocked(visiblePaymentlines[0])}
                      terminals={posTerminals}
                      isTapOnPhoneAvailable={isTapOnPhoneAvailable}
                      onMethodTap={(method) => {
                        handleMethodTap(visiblePaymentlines[0], method, tenderKindLabel(method.kind));
                      }}
                    />
                  )}
                  <OrderSummary
                    order={tableOrder}
                    orderlines={orderlines}
                    paymentlines={paymentlines}
                    discount={discount}
                    itemTotalMode="after-discount"
                    fixed={false}
                  />
                </div>
              )}
            </>
          ) : isReceiptScreenOpen && tableOrder ? (
            /* Summary: Receipt mode view.
               Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection */
            <ReceiptScreen
              order={tableOrder}
              orderlines={orderlines}
              paymentlines={paymentlines}
              discount={discount}
              pendingApprovalOrderlineIds={pendingApprovalOrderlineIds}
              hasPendingGlobalDiscount={hasPendingGlobalDiscount}
            />
          ) : (
            /* Summary: Menu/cart mode with product grid, editable cart, and action row.
               Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-posorderssection */
            <>
            {categoryRail.length > 2 && (
              <nav className={orderClasses.categoryTabs} aria-label="קטגוריות">
                {categoryRail.map((item) => (
                  <UnstyledButton
                    key={item.key}
                    component="button"
                    type="button"
                    data-testid={`category-tab-${item.key}`}
                    className={`${orderClasses.categoryTab} ${categoryFilter === item.filter || selectedAncestorIds.has(item.filter as number) ? orderClasses.categoryTabActive : ''}`}
                    onClick={() => handleCategorySelect(item.filter)}
                  >
                    {item.happyHour && happyHourEffective && (
                      <FontAwesomeIcon icon={faStar as IconProp} className={orderClasses.categoryHappyHourStarInline} />
                    )}
                    {item.label}
                  </UnstyledButton>
                ))}
              </nav>
            )}
            {displayCategoryChildRails.map((rail, railIndex) => (
              <nav key={`child-rail-${railIndex}`} className={orderClasses.categoryTabsChild} aria-label="תת-קטגוריות">
                {rail.map((item) => (
                  <UnstyledButton
                    key={item.key}
                    component="button"
                    type="button"
                    data-testid={`category-tab-child-${item.key}`}
                    className={`${orderClasses.categoryTab} ${categoryFilter === item.filter || selectedAncestorIds.has(item.filter as number) ? orderClasses.categoryTabActive : ''}`}
                    onClick={() => handleCategorySelect(item.filter)}
                  >
                    {item.happyHour && happyHourEffective && (
                      <FontAwesomeIcon icon={faStar as IconProp} className={orderClasses.categoryHappyHourStarInline} />
                    )}
                    {item.label}
                  </UnstyledButton>
                ))}
              </nav>
            ))}
            <div
              className={`${orderClasses.body} ${
                childRailColumnsForLayout >= 2
                  ? orderClasses.bodyWithTwoSubCategories
                  : childRailColumnsForLayout > 0
                    ? orderClasses.bodyWithSubCategories
                    : ''
              }`}
            >
              <section data-testid="order-product-grid" className={`${orderClasses.panel} ${orderClasses.productsPanel}`}>
                <div className={orderClasses.productsHeader}>
                  <TextInput
                    placeholder="חיפוש מוצרים..."
                    value={productSearch}
                    data-testid="order-product-search"
                    onChange={(event) => onProductSearchChange(event.currentTarget.value)}
                    onFocus={handleSearchFocus}
                    onContextMenu={(event) => event.preventDefault()}
                    onSelect={(event) => {
                      const input = event.currentTarget;
                      const end = input.value.length;
                      if (input.selectionStart !== end || input.selectionEnd !== end) {
                        input.setSelectionRange(end, end);
                      }
                    }}
                    radius="md"
                    aria-label="חיפוש מוצרים"
                    classNames={{
                      root: orderClasses.productSearchBar,
                      wrapper: orderClasses.productSearchInputContainer,
                      input: orderClasses.productSearchInput,
                    }}
                  />
                </div>
                <div className={orderClasses.productsBody}>
                  {categoryRail.length > 2 && (
                    <nav className={orderClasses.categoryRail} aria-label="קטגוריות">
                      {categoryRail.map((item) => (
                        <UnstyledButton
                          key={item.key}
                          component="button"
                          type="button"
                          data-testid={`category-rail-${item.key}`}
                          className={`${orderClasses.categoryRailItem} ${categoryFilter === item.filter || selectedAncestorIds.has(item.filter as number) ? orderClasses.categoryRailItemActive : ''}`}
                          onClick={() => handleCategorySelect(item.filter)}
                        >
                          {item.label}
                        </UnstyledButton>
                      ))}
                    </nav>
                  )}
                  {displayCategoryChildRails.map((rail, railIndex) => (
                    <nav key={`child-rail-${railIndex}`} className={orderClasses.categoryRailChild} aria-label="תת-קטגוריות">
                      {rail.map((item) => (
                        <UnstyledButton
                          key={item.key}
                          component="button"
                          type="button"
                          data-testid={`category-rail-child-${item.key}`}
                          className={`${orderClasses.categoryRailItem} ${categoryFilter === item.filter || selectedAncestorIds.has(item.filter as number) ? orderClasses.categoryRailItemActive : ''}`}
                          onClick={() => handleCategorySelect(item.filter)}
                        >
                          {item.label}
                        </UnstyledButton>
                      ))}
                    </nav>
                  ))}
                  <div className={orderClasses.productsList}>
                    {isLoadingPosProducts && <p className={orderClasses.productsEmpty}>טוען מוצרים...</p>}
                    {!isLoadingPosProducts && posProducts.length === 0 && (
                      <p className={orderClasses.productsEmpty}>לא הוגדרו מוצרים פעילים.</p>
                    )}
                    {!isLoadingPosProducts && posProducts.length > 0 && !hasMatchingProducts && (
                      <p className={orderClasses.productsEmpty}>לא נמצאו מוצרים תואמים</p>
                    )}
                    {productGroups.map((group) => (
                      <React.Fragment key={group.key}>
                        {categoryRail.length > 2 && group.title && group.products.length > 0 && (
                          <div className={orderClasses.categorySeparator} data-testid={`category-separator-${group.key}`}>
                            {group.title}
                          </div>
                        )}
                        {group.products.map((product) => {
                          const limit = product.limit ?? null;
                          const isOutOfStock = limit === 0;
                          const showRemaining = limit !== null && limit > 0;
                          const isProductDisabled = isAddingOrderline || isOutOfStock;
                          return (
                            <div
                              key={product.id}
                              data-testid={`pos-product-${product.id}`}
                              role="button"
                              tabIndex={isProductDisabled ? -1 : 0}
                              aria-disabled={isProductDisabled}
                              onClick={isProductDisabled ? undefined : () => onAddOrderline(tableOrder.id, product.id)}
                              onKeyDown={
                                isProductDisabled
                                  ? undefined
                                  : (event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onAddOrderline(tableOrder.id, product.id);
                                      }
                                    }
                              }
                              className={`${orderClasses.productRow} ${isOutOfStock ? orderClasses.productRowOut : ''}`}
                            >
                              <span
                                className={`${orderClasses.productAdd} ${isOutOfStock ? orderClasses.productAddOut : ''}`}
                                aria-hidden="true"
                              >
                                +
                              </span>
                              <span className={orderClasses.productInfo}>
                                <span className={orderClasses.productName}>{product.name}</span>
                                {showRemaining && (
                                  <span className={orderClasses.productRemaining}>{limit} נותרו</span>
                                )}
                                {isOutOfStock && (
                                  <span className={orderClasses.productOutOfStock}>אזל מהמלאי</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                {!isTakeAway && (
                <div className={orderClasses.sendToKitchenWrap}>
                  <div
                    className={orderClasses.sendToKitchenLauncher}
                  >
                    {isFireMenuOpen && (
                      <div
                        className={orderClasses.fireSwipeOverlay}
                        onClick={closeFireMenu}
                        dir="ltr"
                      >
                        <div className={orderClasses.fireSwipeColumn} onClick={(event) => event.stopPropagation()}>
                          <div className={orderClasses.fireSwipeRail}>
                            <span className={`${orderClasses.fireSwipeHint} ${activeFireChoice === 'red' ? orderClasses.fireSwipeHintActive : ''}`} aria-hidden="true">
                              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                            </span>
                            <div
                              role="button"
                              tabIndex={0}
                              data-testid="order-fire-red"
                              aria-label="הדפס אדום ללא שליחה למטבח"
                              className={`${orderClasses.fireOption} ${orderClasses.fireOptionRed} ${activeFireChoice === 'red' ? orderClasses.fireOptionActive : ''}`}
                              style={{ transform: fireDragChoice === 'red' ? `translateX(${fireDragOffset}px)` : undefined }}
                              onPointerDown={handleFireSwipePointerDown('red')}
                              onPointerMove={handleFireSwipePointerMove}
                              onPointerUp={handleFireSwipePointerUp}
                              onPointerCancel={handleFireSwipePointerUp}
                              onClick={(event) => {
                                event.stopPropagation();
                                executeFireChoice('red');
                              }}
                            >
                              <span className={orderClasses.fireOptionIcon}>
                                <FontAwesomeIcon icon={faPrint as IconProp} />
                              </span>
                              <span className={orderClasses.fireOptionLabel}>אדום</span>
                            </div>
                          </div>

                          <div className={orderClasses.fireSwipeRail}>
                            <span className={`${orderClasses.fireSwipeHint} ${activeFireChoice === 'green' ? orderClasses.fireSwipeHintActive : ''}`} aria-hidden="true">
                              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                            </span>
                            <div
                              role="button"
                              tabIndex={0}
                              data-testid="order-fire-green"
                              aria-label="הדפס ושלח למטבח"
                              className={`${orderClasses.fireOption} ${orderClasses.fireOptionGreen} ${activeFireChoice === 'green' ? orderClasses.fireOptionActive : ''}`}
                              style={{ transform: fireDragChoice === 'green' ? `translateX(${fireDragOffset}px)` : undefined }}
                              onPointerDown={handleFireSwipePointerDown('green')}
                              onPointerMove={handleFireSwipePointerMove}
                              onPointerUp={handleFireSwipePointerUp}
                              onPointerCancel={handleFireSwipePointerUp}
                              onClick={(event) => {
                                event.stopPropagation();
                                executeFireChoice('green');
                              }}
                            >
                              <span className={orderClasses.fireOptionIcon}>
                                <FontAwesomeIcon icon={faPrint as IconProp} />
                              </span>
                              <span className={orderClasses.fireOptionLabel}>ירוק</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      ref={sendToKitchenFabRef}
                      type="button"
                      data-testid="order-send-to-kitchen"
                      className={orderClasses.sendToKitchenFab}
                      disabled={isSendActionDisabled}
                      onClick={() => {
                        if (isFireMenuOpen) {
                          closeFireMenu();
                        } else {
                          openFireMenu();
                        }
                      }}
                      aria-label={isSendingToKitchen ? 'שולח למטבח...' : 'שלח למטבח'}
                      title={isSendingToKitchen ? 'שולח למטבח...' : 'שלח למטבח'}
                    >
                      <img src="/greenfire.png" alt="" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                )}
              </section>

              <div className={orderClasses.cartColumn}>
                <section data-testid="order-cart" className={`${orderClasses.panel} ${orderClasses.cartPanel}`}>
                  {isLoadingOrderlines && !hasVisibleOrderlines && (
                    <p className={orderClasses.cartEmpty}>טוען פריטים...</p>
                  )}
                  {!isLoadingOrderlines && !hasVisibleOrderlines && (
                    <p className={orderClasses.cartEmpty}>אין פריטים עדיין. הקש על מוצר כדי להוסיף.</p>
                  )}
                  {hasVisibleOrderlines && (
                    <ul ref={cartListRef} data-testid="order-cart-list" className={orderClasses.cartList}>
                      {orderlines.map((line) => {
                        const isSent = line.status === 'sent' || line.status === 'skipped';
                        const originalLineTotal = line.unitPrice * line.quantity;
                        const hasDiscount = line.discountInput != null && line.discountInput > 0;
                        const expectedHappyHourDiscount = line.happyHourDiscountPct != null && line.happyHourDiscountPct > 0
                          ? Math.round(originalLineTotal * line.happyHourDiscountPct / 100 * 10) / 10
                          : null;
                        const hasHappyHourDiscount = hasDiscount &&
                          expectedHappyHourDiscount !== null &&
                          line.discountInput === expectedHappyHourDiscount;
                        const lineTotal = hasDiscount
                          ? originalLineTotal - line.discountInput!
                          : originalLineTotal;
                        const lineProductLimit = line.productId != null ? (productLimitById.get(line.productId) ?? null) : null;
                        const isLineOutOfStock = lineProductLimit === 0;
                        const selectionGroups = groupOrderlineSelections(line.selections);
                        const hasSelections = (line.selections?.length ?? 0) > 0;
                        const hasPendingApproval = pendingApprovalOrderlineIds.has(line.id);
                        return (
                          <li
                            key={line.id}
                            data-testid={`orderline-${line.id}`}
                            data-status={line.status}
                            className={`${orderClasses.cartLine} ${isSent ? orderClasses.cartLineSent : ''}`}
                            onPointerDown={handleLineLongPressStart(line)}
                            onPointerMove={handleLineLongPressMove}
                            onPointerUp={clearLongPress}
                            onPointerCancel={clearLongPress}
                            onPointerLeave={clearLongPress}
                          >
                            <div className={orderClasses.cartLineLeft}>
                              <span className={orderClasses.cartLinePrice}>{formatMoney(lineTotal)}</span>
                              {isSent && <span className={orderClasses.cartLineQuantity}>כמות: {line.quantity}</span>}
                            </div>
                            {!isSent && (
                              <div className={orderClasses.qtyStepper}>
                                <button
                                  type="button"
                                  data-testid={`orderline-${line.id}-increment`}
                                  className={`${orderClasses.qtyButton} ${orderClasses.qtyButtonInc}`}
                                  disabled={isAdjustingOrderline || isLineOutOfStock || line.quantity >= 99}
                                  onClick={() => onIncrementOrderline(line)}
                                  aria-label={`הוסף כמות — ${orderlineDisplayName(line)}`}
                                >
                                  +
                                </button>
                                <button
                                  type="button"
                                  data-testid={`orderline-${line.id}-quantity`}
                                  className={`${orderClasses.qtyValue} ${orderClasses.qtyValueButton}`}
                                  disabled={isAdjustingOrderline}
                                  onClick={() => setQuantityEditLine(line)}
                                  aria-label={`ערוך כמות — ${orderlineDisplayName(line)}`}
                                >
                                  {line.quantity}
                                </button>
                                <button
                                  type="button"
                                  data-testid={`orderline-${line.id}-decrement`}
                                  className={`${orderClasses.qtyButton} ${orderClasses.qtyButtonDec}`}
                                  disabled={isAdjustingOrderline}
                                  onClick={() => onDecrementOrderline(line)}
                                  aria-label={`הפחת כמות — ${orderlineDisplayName(line)}`}
                                >
                                  -
                                </button>
                              </div>
                            )}
                            <div className={orderClasses.cartLineBody}>
                              <div className={orderClasses.cartLineNameRow}>
                <span className={orderClasses.cartLineName}>
                                  {orderlineDisplayName(line)}
                                </span>
                              </div>
                              {selectionGroups.length > 0 && (
                                <ul className={orderClasses.selectionGroups}>
                                  {selectionGroups.map((group, index) => (
                                    <li key={`${group.groupName}-${index}`} className={orderClasses.selectionOptionGroup}>
                                      {group.groupName && (
                                        <span className={orderClasses.selectionGroupLabel}>{group.groupName}:</span>
                                      )}
                                      <span className={orderClasses.selectionOptionRow}>
                                        <span className={orderClasses.selectionOptionName}>
                                          {group.options.map((opt) => opt.name).join(', ')}
                                        </span>
                                        <span className={orderClasses.selectionOptionPrice}>+{formatMoney(groupOrderlineExtraTotal(group, line.quantity))}</span>
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <span className={orderClasses.cartLineMeta}>
                                <span className={orderClasses.metaUnitQty}>
                                  <span>יח׳</span>
                                  <span>{line.quantity}</span>
                                </span>
                                {' x '}
                                {formatMoney(lineTotal / line.quantity)} ₪
                              </span>
                              {hasHappyHourDiscount && (
                                <span className={orderClasses.happyHourDiscountMeta}>
                                  Happy Hour {formatMoneyCompact(line.happyHourDiscountPct!)}%
                                </span>
                              )}
                              {isSent ? (
                                <>
                                  {hasDiscount && !hasHappyHourDiscount && (
                                    <span className={orderClasses.discountBadge}>
                                      הנחה ₪{formatMoneyCompact(line.discountInput!)}
                                    </span>
                                  )}
                                  <span className={orderClasses.statusRow}>
                                    {hasPendingApproval && (
                                      <PendingApprovalBadge />
                                    )}
                                    <span data-testid={`orderline-${line.id}-sent-badge`} className={`${orderClasses.statusPill} ${orderClasses.statusPillSent}`}>
                                      • נשלח
                                    </span>
                                  </span>
                                </>
                              ) : (
                                <span className={orderClasses.statusRow}>
                                  {hasPendingApproval && (
                                    <PendingApprovalBadge />
                                  )}
                                  <span className={`${orderClasses.statusPill} ${orderClasses.statusPillNew}`}>• חדש</span>
                                </span>
                              )}
                            </div>
                            <div className={orderClasses.cartLineButtons}>
                              {line.note && line.note.trim().length > 0 && (
                                <UnstyledButton
                                  component="button"
                                  type="button"
                                  data-testid={`orderline-${line.id}-note`}
                                  className={orderClasses.noteButton}
                                  onClick={() => setNoteEditLine(line)}
                                  aria-label={`ערוך הערה — ${orderlineDisplayName(line)}`}
                                  title={line.note}
                                >
                                  <FontAwesomeIcon icon={faCommentDots as IconProp} />
                                </UnstyledButton>
                              )}
                            </div>
                            {!isSent && hasSelections && line.productId != null && (
                              <UnstyledButton
                                component="button"
                                type="button"
                                data-testid={`orderline-${line.id}-edit-selections`}
                                className={orderClasses.editSelectionsButton}
                                disabled={isAdjustingOrderline}
                                onClick={() => onEditOrderlineSelections(line)}
                                aria-label={`ערוך בחירות — ${orderlineDisplayName(line)}`}
                                title="ערוך בחירות"
                              >
                                <FontAwesomeIcon icon={faPen as IconProp} />
                              </UnstyledButton>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {hasVisibleOrderlines && (
                    <button
                      type="button"
                      className={orderClasses.totalRow}
                      aria-label="פתיחת מסך תשלום"
                      data-testid="order-total-row"
                      onClick={handlePaymentEntry}>
                      <span>סה"כ</span>
                      <span data-testid="order-total">{formatMoney(billableCartTotal)}</span>
                    </button>
                  )}
                  {!hasVisibleOrderlines && !isLoadingOrderlines && orderlinesLoadedOrderId === tableOrder.id && (
                    <Button
                      color="black"
                      radius="md"
                      fullWidth
                      className={orderClasses.releaseTableButton}
                      data-testid="order-release-table"
                      disabled={isClosingOrder}
                      onClick={() => onCloseOrder(tableOrder.id)}
                    >
                      {isClosingOrder ? 'משחרר...' : isTakeAway ? 'שחרר הזמנה' : 'שחרר שולחן'}
                    </Button>
                  )}

                </section>

                {hasVisibleOrderlines && (onUpdateDiners || onTransfer) && (
                  <div className={orderClasses.actionsSection}>
                    {onUpdateDiners && (
                      <button
                        type="button"
                        className={orderClasses.actionButton}
                        data-testid="order-diners"
                        disabled={isUpdatingDiners}
                        onClick={() => setIsDinersNumPadOpen(true)}
                      >
                        <span>סועדים ({currentDinersCount ?? 1})</span>
                      </button>
                    )}
                    {onTransfer && !isTakeAway && (
                      <UnstyledButton
                        component="button"
                        type="button"
                        className={`${orderClasses.actionButton} ${!hasCancelableLines || isMergedTable ? orderClasses.actionButtonDisabled : ''}`}
                        aria-disabled={!hasCancelableLines || isMergedTable || undefined}
                        disabled={isTransferring || isMergedTable}
                        data-testid="order-transfer"
                        onClick={openTransfer}
                      >
                        <span>העבר שולחן</span>
                      </UnstyledButton>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className={orderClasses.actionButton}
                  data-testid="order-actions-open"
                  onClick={openOrderActions}
                >
                  <span>פעולות</span>
                  {hasPendingGeneralItem && (
                    <PendingApprovalBadge />
                  )}
                </button>
              </div>
            </div>
            </>
          )
        ) : (
          <div className={orderClasses.openOrderPlaceholder}>
            <Button radius="md" size="md" data-testid="order-open" onClick={onOpenOrder} disabled={isOpeningOrder || !activeSession}>
              {isOpeningOrder ? 'פותח...' : 'פתח הזמנה'}
            </Button>
          </div>
        )}

        <div className={orderClasses.bottomBar} data-testid="order-bottom-bar">
          <button
            type="button"
            className={orderClasses.bottomItem}
            data-testid="order-bottom-table"
            onClick={onBackToAreas}
            aria-label={`חזרה להזמנות — שולחן ${tableLabel}`}
          >
            <span className={orderClasses.tableBadge}>{tableLabel}</span>
            <span className={orderClasses.bottomLabel}>שולחן</span>
          </button>

          <AverageBreakdownButton
            ref={averageBreakdownRef}
            average={tableAverage}
            rows={averageBreakdownRows}
            isOpen={isAverageBreakdownOpen}
            bottomItemClassName={orderClasses.bottomItem}
            bottomNumberClassName={orderClasses.bottomNumber}
            bottomLabelClassName={orderClasses.bottomLabel}
            onToggle={() => setIsAverageBreakdownOpen((open) => !open)}
          />

          <button
            type="button"
            className={`${orderClasses.bottomItem} ${!isPaymentScreenOpen && !isReceiptScreenOpen ? orderClasses.bottomItemActive : ''}`}
            data-testid="order-bottom-menu"
            disabled={!tableOrder || isQuickChargeOrder}
            onClick={onMenuScreen}
          >
            <span className={orderClasses.bottomIcon}>
              <img src="/menu.png" alt="" aria-hidden="true" />
            </span>
            <span className={orderClasses.bottomLabel}>תפריט</span>
          </button>

          <button
            type="button"
            className={`${orderClasses.bottomItem} ${isReceiptScreenOpen && !isPaymentScreenOpen ? orderClasses.bottomItemActive : ''}`}
            data-testid="order-bottom-receipt"
            disabled={!tableOrder || !hasSentOrderlines}
            onClick={() => {
              if (tableOrder) {
                onOpenReceiptScreen(tableOrder.id);
              }
            }}
          >
            <span className={orderClasses.bottomIcon}>
              <img src="/bill.png" alt="" aria-hidden="true" />
            </span>
            <span className={orderClasses.bottomLabel}>חשבון</span>
          </button>

          <button
            type="button"
            className={`${orderClasses.bottomItem} ${isPaymentScreenOpen ? orderClasses.bottomItemActive : ''}`}
            data-testid="order-bottom-payment"
            disabled={!tableOrder}
            onClick={handlePaymentEntry}
          >
            <span className={orderClasses.bottomIcon}>
              <FontAwesomeIcon icon={faShekelSign as IconProp} style={{ width: '1.5em', height: '1.5em' }} />
            </span>
            <span className={orderClasses.bottomLabel}>תשלום</span>
          </button>


        </div>

        {isUnsentPaymentPromptOpen && (
          <div
            className={orderClasses.splitDialogBackdrop}
            role="presentation"
            onClick={() => !isQueueingKitchenPrint && setIsUnsentPaymentPromptOpen(false)}
          >
            <section
              className={`${orderClasses.splitDialog} ${orderClasses.unsentPaymentDialog}`}
              role="dialog"
              aria-modal="true"
              aria-label="פריטים שעדיין לא נשלחו"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                data-testid="unsent-payment-close"
                className={orderClasses.splitDialogClose}
                onClick={() => setIsUnsentPaymentPromptOpen(false)}
                disabled={isQueueingKitchenPrint}
                aria-label="סגור"
              >
                <FontAwesomeIcon icon={faTimes as IconProp} />
              </button>

              <header className={orderClasses.unsentPaymentHeader}>
                <span className={orderClasses.unsentPaymentIcon} aria-hidden="true">🍽️</span>
                <h2 className={orderClasses.unsentPaymentTitle}>
                  {hasSentOrderlines ? 'יש פריטים שלא נשלחו' : 'הזמנה לא נשלחה'}
                </h2>
              </header>

              <div className={orderClasses.unsentPaymentItemsSection}>
                <h3>פריטים שלא נשלחו</h3>
                <ul className={orderClasses.unsentPaymentItems}>
                  {pendingOrderlines.map((line) => (
                    <li key={line.id}>
                      <span className={orderClasses.unsentPaymentItemDot} aria-hidden="true" />
                      <span>{orderlineDisplayName(line)} ×{line.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={orderClasses.unsentPaymentActions}>
                <SharedButton
                  variant="confirm"
                  size="lg"
                  fullWidth
                  loading={isQueueingKitchenPrint}
                  loadingLabel="שולח למטבח..."
                  dataTestId="unsent-payment-send-now"
                  onClick={() => {
                    setIsUnsentPaymentPromptOpen(false);
                    return handlePrintKitchenDirect();
                  }}
                >
                  שלח עכשיו
                </SharedButton>
                {hasSentOrderlines && tableOrder && (
                  <SharedButton
                    variant="save"
                    size="lg"
                    fullWidth
                    disabled={isQueueingKitchenPrint}
                    dataTestId="unsent-payment-continue"
                    onClick={() => {
                      setIsUnsentPaymentPromptOpen(false);
                      onOpenPaymentScreen(tableOrder.id);
                    }}
                  >
                    המשך לתשלום ללא שליחה
                  </SharedButton>
                )}
              </div>
            </section>
          </div>
        )}

        <NumPadModal
          isOpen={quantityEditLine !== null}
          title="עדכון כמות"
          subjectName={quantityEditLine ? orderlineDisplayName(quantityEditLine) : ''}
          initialValue={quantityEditLine?.quantity ?? 1}
          allowUnlimited={false}
          maxDigits={2}
          maxDigitsMessage="הכמות המקסימלית היא 99"
          isSubmitting={isAdjustingOrderline}
          onClose={() => setQuantityEditLine(null)}
          onConfirm={(value) => {
            if (!quantityEditLine || value === null) {
              return;
            }
            const clampedQuantity = Math.min(99, Math.max(0, value));
            onSetOrderlineQuantity(quantityEditLine, clampedQuantity);
            setQuantityEditLine(null);
          }}
        />

        {onUpdateDiners && (
          <NumPadModal
            isOpen={isDinersNumPadOpen}
            dataTestId="order-diners-numpad"
            title="מספר סועדים"
            subjectName="סועדים"
            initialValue={currentDinersCount ?? 1}
            allowUnlimited={false}
            minValue={1}
            maxDigits={2}
            confirmLabel="עדכן"
            isSubmitting={isUpdatingDiners}
            onClose={() => setIsDinersNumPadOpen(false)}
            onConfirm={(value) => {
              if (value === null || value < 1 || value > 99) return;
              setIsDinersNumPadOpen(false);
              if (tableOrder) onUpdateDiners(value);
            }}
          />
        )}

        <SplitPaymentDialog
          isOpen={isSplitDialogOpen}
          totalAmount={paymentAmount}
          equalSplitTotal={splitGrandTotal}
          canOpenByProduct={canOpenByProductSplit}
          isSubmitting={isSplittingPayment}
          onClose={() => {
            setIsSplitDialogOpen(false);
            setSelectedSplitPaymentlineId(null);
          }}
          onOpenByProduct={handleOpenByProductSplit}
          onConfirmEqual={handleConfirmEqualSplit}
          onConfirmCustom={handleConfirmCustomSplit}
        />

        <AmountEntryDialog
          isOpen={amountEntryTarget !== null}
          lineFullTotal={amountEntryTarget?.lineFullTotal ?? 0}
          methodLabel={amountEntryTarget?.methodLabel ?? ''}
          allowOverage={amountEntryTarget?.method.kind === 'cash'}
          isSubmitting={isMarkingPaymentDone || isSplittingPayment}
          onClose={() => setAmountEntryTarget(null)}
          onConfirm={handleAmountEntryConfirm}
        />

        <CashChangeDialog
          isOpen={cashChangeTarget !== null}
          tenderedAmount={cashChangeTarget?.tenderedAmount ?? 0}
          dueAmount={cashChangeTarget?.dueAmount ?? 0}
          isSubmitting={isMarkingPaymentDone}
          onReturnChange={() => { void handleCashChangeDecision('return_change'); }}
          onAddToTip={() => { void handleCashChangeDecision('add_to_tip'); }}
          onCancel={() => setCashChangeTarget(null)}
        />

        <ItemDiscountSelectModal
          isOpen={isItemPaymentSelectOpen}
          title="תשלום לפי פריטים"
          confirmLabel="פצל"
          showSteps={false}
          hideCloseButton
          orderlines={itemPaymentLines}
          isSelectableLine={(line) => line.status !== 'pending'}
          getSelectableQuantity={getSelectableItemPaymentQuantity}
          resolveSubtotal={computeItemPaymentSplitAmount}
          maxSubtotal={getItemPaymentSelectionCap(paymentAmount)}
          hideSelectAll
          emptyMessage="אין פריטים זמינים לפיצול בשורת תשלום זו."
          isSubmitting={isSplittingPayment}
          onClose={() => setIsItemPaymentSelectOpen(false)}
          onConfirm={handleConfirmByProductSplit}
        />

        {tipTarget && (
          <TipModal
            isOpen
            title={tipTarget.scope === 'order' ? 'טיפ כללי' : 'טיפ שורה'}
            showScope={tipTarget.scope === 'order'}
            base={tipTarget.scope === 'order' ? billableCartTotal : tipTarget.line.amount}
            currentMode={
              tipTarget.scope === 'order'
                ? tableOrder?.tipInputMode ?? null
                : tipTarget.line.tipInputMode ?? null
            }
            currentValue={
              tipTarget.scope === 'order'
                ? tableOrder?.tipInputValue ?? null
                : tipTarget.line.tipInputValue ?? null
            }
            isSubmitting={isSettingTip}
            onClose={() => setTipTarget(null)}
            onConfirm={(payload) => {
              if (tipTarget.scope === 'order') {
                void onSetOrderTip(payload);
              } else {
                void onSetPaymentlineTip(tipTarget.line.id, payload);
              }
              setTipTarget(null);
            }}
          />
        )}

        <KitchenNoteModal
          isOpen={noteEditLine !== null}
          productName={noteEditLine ? orderlineDisplayName(noteEditLine) : ''}
          initialNote={noteEditLine?.note ?? null}
          isSaving={isAdjustingOrderline}
          onClose={() => setNoteEditLine(null)}
          onConfirm={handleConfirmKitchenNote}
        />

        {tableOrder && (
          <div aria-hidden="true" className={kdsPageClasses.printCaptureOffscreen}>
            <KdsPrintTicket
              printerName={kitchenPrinterName}
              tableNumber={tableOrder.tableNumber ?? tableOrder.orderCode}
              orderNumber={fallbackKitchenPreviewOrderNumber}
              dinersCount={dinersCount}
              printTime={new Date()}
              waiterName={currentEmployeeName ?? tableOrder.openedByName ?? undefined}
              locale="he-IL"
              labels={printTicketLabels}
              orderlines={kitchenPreviewOverrideLines ?? effectiveKitchenPreviewLines}
              receiptPaperRef={printReceiptPaperRef}
            />
          </div>
        )}
      </section>

      {isResetPaymentConfirmOpen && (
        <div
          className={orderClasses.splitDialogBackdrop}
          role="presentation"
          onClick={() => !isResettingPayment && setIsResetPaymentConfirmOpen(false)}
        >
          <section
            className={`${orderClasses.splitDialog} ${orderClasses.resetPaymentConfirmDialog}`}
            role="dialog"
            aria-modal="true"
            aria-label="איפוס תשלום"
            onClick={(event) => event.stopPropagation()}
          >
            <header className={orderClasses.resetPaymentConfirmHeader}>
              <h2>איפוס</h2>
            </header>
            <p className={orderClasses.resetPaymentConfirmText}>
              לחיצה על איפוס תצמצם את כל השורות המפוצלות שלא שולמו לשורה אחת ותמחק טיפ שלא שולם, האם ברצונך להמשיך?
            </p>
            <div className={orderClasses.resetPaymentConfirmActions}>
              <SharedButton
                variant="cancel"
                size="lg"
                fullWidth
                disabled={isResettingPayment}
                dataTestId="reset-payment-cancel"
                onClick={() => setIsResetPaymentConfirmOpen(false)}
              >
                בטל
              </SharedButton>
              <SharedButton
                variant="save"
                size="lg"
                fullWidth
                loading={isResettingPayment}
                loadingLabel="מאפס..."
                dataTestId="reset-payment-confirm"
                onClick={async () => {
                  setIsResettingPayment(true);
                  try {
                    await onResetOrderTip();
                    setIsResetPaymentConfirmOpen(false);
                  } finally {
                    setIsResettingPayment(false);
                  }
                }}
              >
                המשך
              </SharedButton>
            </div>
          </section>
        </div>
      )}

      <ReceiptActionsModal
        opened={isActionsOpen}
        onClose={() => setIsActionsOpen(false)}
        onSelectAction={(key) => {
          setIsActionsOpen(false);
          if (key === 'globalDiscount') setIsGlobalDiscountOpen(true);
          if (key === 'itemDiscount') setIsItemSelectOpen(true);
          if (key === 'oth') setIsOthSelectOpen(true);
          if (key === 'cancel') setIsCancelSelectOpen(true);
        }}
        isTakeAway={isTakeAway}
        isQuickCharge={isQuickChargeOrder}
      />

      <OrderActionsModal
        opened={isOrderActionsOpen}
        onClose={() => setIsOrderActionsOpen(false)}
        onSelectAction={(key) => {
          setIsOrderActionsOpen(false);
          if (key === 'generalItem') {
            if (!isManager && approvals.hasPendingRequest) {
              showPendingApprovalToast();
              return;
            }
            setIsGeneralItemOpen(true);
          }
          if (key === 'printLog') {
            void handleOpenPrintLog();
          }
          if (key === 'reprint') {
            setIsReprintSelectOpen(true);
          }
          if (key === 'tipOnly' && tableOrder) {
            onOpenPaymentScreen(tableOrder.id, { tipOnly: true });
          }
        }}
        isManager={isManager}
        isTakeAway={isTakeAway}
        isVip={tableOrder?.isVip ?? false}
        waiterName={tableOrder?.openedByName ?? null}
        onToggleVip={(next) => {
          if (tableOrder) {
            onToggleOrderVip?.(tableOrder.id, next);
          }
        }}
        hasDeposit={tableOrder?.hasDeposit ?? false}
        onToggleDeposit={(next) => {
          if (tableOrder) {
            onToggleDeposit?.(tableOrder.id, next);
          }
        }}
        generalItemHasPending={hasPendingGeneralItem}
        canAddTipOnly={isTipOnlyOrder && isManager}
      />

      <GeneralItemModal
        isOpen={isGeneralItemOpen}
        isSaving={isAddingGeneralItem}
        onClose={() => setIsGeneralItemOpen(false)}
        onConfirm={handleAddGeneralItem}
      />

      <Modal
        opened={isRefundCodeOpen}
        onClose={() => {
          if (isAddingGeneralItem) {
            return;
          }
          setIsRefundCodeOpen(false);
          setPendingRefundPayload(null);
          setRefundCodeInput('');
        }}
        title={null}
        centered
        size="md"
        withCloseButton={false}
        classNames={{
          content: orderClasses.refundCodeModal,
          body: orderClasses.refundCodeModalBody,
        }}
      >
        <div className={orderClasses.refundCodePanel}>
          <div className={orderClasses.refundCodeHeader}>
            <span className={orderClasses.refundCodeBadge} aria-hidden="true">
              <FontAwesomeIcon icon={faShieldHalved as IconProp} />
            </span>
            <h3 className={orderClasses.refundCodeTitle}>הזן קוד אימות</h3>
            <p className={orderClasses.refundCodeHint}>Verification code sent to</p>
            <p className={orderClasses.refundCodeHintEmail}>{pendingRefundPayload?.ownerEmail ?? 'owner email'}</p>
          </div>

          <div className={orderClasses.refundCodeBody}>
            <div
              className={orderClasses.refundCodeDisplay}
              data-testid="refund-code-input"
              aria-label="קוד אימות"
            >
              <div className={orderClasses.refundCodeSlots}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <span key={index} className={orderClasses.refundCodeSlot}>
                    {refundCodeInput[index] ?? ''}
                  </span>
                ))}
              </div>
            </div>

            <NumericKeypad
              keypadClassName={orderClasses.refundCodeKeypad}
              keyClassName={orderClasses.refundCodeKey}
              disabled={isAddingGeneralItem}
              onDigit={appendRefundDigit}
              onBackspace={backspaceRefundDigit}
              backspaceDisabled={refundCodeInput.length === 0}
              backspaceAriaLabel="מחק ספרה"
              backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
              leftKey={{
                label: 'C',
                className: orderClasses.refundCodeKey,
                onClick: clearRefundCode,
                disabled: isAddingGeneralItem,
                ariaLabel: 'נקה קוד',
              }}
            />

            <div className={orderClasses.refundCodeActions}>
              <Button
                onClick={() => {
                  void handleConfirmRefundCode();
                }}
                disabled={isAddingGeneralItem || refundCodeInput.trim().length !== 6}
                loading={isAddingGeneralItem}
                data-testid="refund-code-confirm"
                className={orderClasses.refundCodeConfirmButton}
              >
                אישור
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setIsRefundCodeOpen(false);
                  setPendingRefundPayload(null);
                  setRefundCodeInput('');
                }}
                disabled={isAddingGeneralItem}
                data-testid="refund-code-cancel"
                className={orderClasses.refundCodeCancelButton}
              >
                ביטול
              </Button>
            </div>
          </div>
        </div>

      </Modal>

      <OrderPrintLogModal
        opened={isPrintLogOpen}
        logs={printLogs}
        isLoading={isLoadingPrintLog}
        title={`לוג הדפסות - ${printLogTitleTarget}`}
        onClose={() => setIsPrintLogOpen(false)}
        onOpenItems={setActivePrintLogItems}
      />

      <OrderPrintLogItemsModal
        log={activePrintLogItems}
        onClose={() => setActivePrintLogItems(null)}
      />

      <DepositReturnModal
        opened={isDepositReturnOpen}
        onClose={() => setIsDepositReturnOpen(false)}
        tableNumber={tableOrder?.tableNumber ?? null}
      />

      {approvals.approvalRequestModalState && (
        <ApprovalRequestModal
          opened={approvals.approvalRequestModalState.opened}
          onClose={approvals.closeApprovalRequestModal}
          actionType={approvals.approvalRequestModalState.actionType}
          payload={approvals.approvalRequestModalState.payload}
          orderlines={orderlines}
          isSubmitting={approvals.isSubmittingApproval}
          isManager={isManager}
          onSubmit={approvals.submitApproval}
        />
      )}


      <GlobalDiscountModal
        isOpen={isGlobalDiscountOpen}
        remainingToPay={remainingAfterGlobalDiscount}
        existingDiscountAmount={discount}
        isSaving={isSavingDiscount}
        onClose={() => setIsGlobalDiscountOpen(false)}
        removeDisabledMessage={isManager ? undefined : REMOVE_DISCOUNT_DISABLED_MESSAGE}
        onRemoveDiscount={async () => {
          if (!tableOrder || !employeeSession || discountInFlightRef.current || !isManager) return;
          discountInFlightRef.current = true;
          setIsSavingDiscount(true);
          try {
            await ordersService.updateGlobalDiscount(tenantId, tableOrder.id, null, null, employeeSession);
            setIsGlobalDiscountOpen(false);
          } catch (err) {
            notifications.show({ title: 'שגיאה בהסרת הנחה', message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''), color: 'red', autoClose: 3000 });
          } finally {
            setIsSavingDiscount(false);
            discountInFlightRef.current = false;
          }
        }}
        onConfirm={async (discountAmount, mode, scope, discountInput) => {
          if (!tableOrder || !employeeSession || discountInFlightRef.current) return;
          setIsGlobalDiscountOpen(false);
          const persistenceScope = resolveGlobalDiscountScopeForPayments(scope, paidAmount);
          const persisted = resolveGlobalDiscountPersistence(mode, persistenceScope, discountAmount, discountInput);
          approvals.requestApproval('global_discount', {
            actionType: 'global_discount',
            discountMode: persisted.discountMode ?? 'amount',
            discountInput: persisted.discountInput ?? discountAmount,
            discountAmount,
          });
        }}
      />

      <ItemDiscountSelectModal
        isOpen={isItemSelectOpen}
        title="הנחה לפריטים"
        confirmLabel="המשך"
        hideCloseButton
        orderlines={orderlines}
        isSelectableLine={(line) => line.status === 'sent' || line.status === 'skipped' || (isTakeAway && line.status === 'pending')}
        isRowDisabled={(line) => !isManager && line.unitPrice * line.quantity - (line.discountInput ?? 0) <= 0}
        onClose={() => setIsItemSelectOpen(false)}
        onConfirm={(selections, subtotal) => {
          setPendingItemSelections(selections);
          setItemSelectionSubtotal(subtotal);
          setIsItemSelectOpen(false);
          setIsItemDiscountOpen(true);
        }}
      />

      <ItemDiscountSelectModal
        isOpen={isOthSelectOpen}
        title="על חשבון הבית"
        confirmLabel="אישור"
        showSteps={false}
        hideCloseButton
        orderlines={orderlines}
        isSelectableLine={(line) => line.status === 'sent' || line.status === 'skipped' || (isTakeAway && line.status === 'pending')}
        isRowDisabled={(line) => !isManager && line.unitPrice * line.quantity - (line.discountInput ?? 0) <= 0}
        onClose={() => setIsOthSelectOpen(false)}
        onConfirm={async (selections) => {
          if (!tableOrder || !employeeSession || discountInFlightRef.current) return;
          const items = buildCompedItemDiscountPayload(selections, orderlines);
          // Compare the net comped delta (gross − existing) against the remaining balance.
          const totalCompedDelta = getTotalDiscountDelta(items);
          if (totalCompedDelta > remainingAfterGlobalDiscount) {
            notifications.show({ title: 'הנחה חורגת מהיתרה לתשלום', message: 'ההנחה הנבחרת גבוהה מהסכום שנותר לתשלום', color: 'red', autoClose: 3000 });
            return;
          }
          setIsOthSelectOpen(false);
          approvals.requestApproval('on_the_house', {
            actionType: 'on_the_house',
            items: items.map(({ orderlineId, quantity, discountInput }) => ({ orderlineId, quantity, discountInput })),
          });
        }}
      />

      <ItemDiscountSelectModal
        isOpen={isCancelSelectOpen}
        title="ביטול פריטים"
        confirmLabel="אישור"
        showSteps={false}
        hideCloseButton
        orderlines={orderlines}
        isSelectableLine={isReceiptCancelableOrderline}
        emptyMessage="אין פריטים שנשלחו לביטול."
        isSubmitting={isSavingDiscount}
        onClose={() => setIsCancelSelectOpen(false)}
        onConfirm={async (selections) => {
          setIsCancelSelectOpen(false);
          const cancelScope = resolveCancelScope(orderlines, selections);
          if (cancelScope === 'order' && hasPaidPaymentline(paymentlines)) {
            notifications.show({
              color: 'red',
              title: 'לא ניתן לבטל הזמנה',
              message: 'קיימת שורת תשלום משולמת',
              autoClose: 3000,
            });
            return;
          }
          const payload = buildCancelApprovalPayload(selections, cancelScope);
          approvals.requestApproval('cancel', payload);
        }}
      />

      <ItemDiscountSelectModal
        isOpen={isReprintSelectOpen}
        title="הדפסה חוזרת"
        confirmLabel="הדפסה חוזרת"
        showSteps={false}
        orderlines={orderlines}
        isSelectableLine={(line) => line.status === 'sent' || (isTakeAway && line.status === 'pending')}
        hideCloseButton
        emptyMessage="אין מוצרים שהודפסו על מנת לבצע שליחה חוזרת"
        isSubmitting={isReprinting}
        onClose={() => setIsReprintSelectOpen(false)}
        onConfirm={handleConfirmReprint}
      />

      <ItemDiscountModal
        isOpen={isItemDiscountOpen}
        itemsSubtotal={itemSelectionSubtotal}
        maxDiscountAmount={(() => {
          return getMaxPerUnitDiscount(pendingItemSelections, orderlines);
        })()}
        hasExistingDiscount={hasExistingItemDiscount(pendingItemSelections, orderlines)}
        isSaving={isSavingDiscount}
        onClose={() => setIsItemDiscountOpen(false)}
        onBack={() => { setIsItemDiscountOpen(false); setIsItemSelectOpen(true); }}
        removeDisabledMessage={isManager ? undefined : REMOVE_DISCOUNT_DISABLED_MESSAGE}
        onRemoveDiscount={async () => {
          if (!tableOrder || !employeeSession || discountInFlightRef.current || !isManager) return;
          discountInFlightRef.current = true;
          setIsSavingDiscount(true);
          try {
            await ordersService.applyItemDiscount(
              tenantId,
              tableOrder.id,
              pendingItemSelections.map((sel) => ({ orderlineId: sel.orderlineId, quantity: sel.quantity, discountInput: null })),
              employeeSession,
            );
            setIsItemDiscountOpen(false);
            setPendingItemSelections([]);
          } catch (err) {
            notifications.show({ title: 'שגיאה בהסרת הנחת פריטים', message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''), color: 'red', autoClose: 3000 });
          } finally {
            setIsSavingDiscount(false);
            discountInFlightRef.current = false;
          }
        }}
        onConfirm={async (discountValue, mode) => {
          if (!tableOrder || !employeeSession || discountInFlightRef.current) return;
          const items = buildItemDiscountPayload(pendingItemSelections, orderlines, discountValue, mode);
          const totalDiscountDelta = getTotalDiscountDelta(items);
          if (totalDiscountDelta > remainingAfterGlobalDiscount) {
            notifications.show({ title: 'הנחה חורגת מהיתרה לתשלום', message: 'ההנחה הנבחרת גבוהה מהסכום שנותר לתשלום', color: 'red', autoClose: 3000 });
            return;
          }
          setIsItemDiscountOpen(false);
          approvals.requestApproval('item_discount', {
            actionType: 'item_discount',
            items: items.map(({ orderlineId, quantity, discountInput }) => ({ orderlineId, quantity, discountInput: discountInput ?? 0 })),
          });
          setPendingItemSelections([]);
        }}
      />
    </>
  );
};

export const PosOrdersSection = React.memo(PosOrdersSectionComponent);
